import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import ScrollArea from "../ScrollArea.jsx";
import DataTable from "../DataTable.jsx";
import HoverTooltip from "../HoverTooltip.jsx";
import Select from "../Select.jsx";
import SearchInput from "../ui/SearchInput.jsx";
import { buttonClass } from "../ui/Button.jsx";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { findTraceScrollRoot } from "../TraceTable.jsx";
import { renderTraceCell, traceRowClassName } from "../TraceRow.jsx";
import { useDataTableColumns } from "../../lib/columnSelection.js";
import { useQuery } from "../../lib/dataClient.js";
import { hasPermission, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";
import { analyticsErrorCopy } from "../../lib/analyticsClient.js";
import { formatCompact } from "../../lib/renderers.js";
import { encodeScopePath, scopeStepLabel, useLabels } from "../../lib/dimensionDefs.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { getTriageChatHost, subscribeTriageChatHost } from "../../lib/triageChatStore.js";
import { poolSpan } from "../../lib/triageWindow.js";
import TriageTimeWindow from "../TriageTimeWindow.jsx";
import {
  ACTION_CHIP, BASELINE, CASE_INSET, CountChip, Disclosure, FILE_LABEL, POPOVER_CARD,
  SeverityWord, TIMELINE_BINS, TurnTicks, turnSpanLabel,
} from "./triageParts.jsx";
import { buildTriageMap } from "../../lib/triage.js";
import {
  selectSessions, liteRows, severityRank,
  liteSessionMix,
  inLiteWindow, liteWindowName, liteKept, standardLabel, ASKABLE_VERDICTS, liteVerdictLabel,
} from "../../lib/triageLite.js";
import { fetchTriageStart, fetchVerdicts, MAX_RUN_SCOPE } from "../../lib/findingsTriage.js";
import { LiteSessionPanel, membersOf } from "./LiteSession.jsx";
import { prefetchSessionPage } from "./TriageSessionPreview.jsx";
import { LiteStandardBar, LiteStandardsCard, STANDARDS_CARD_W, boardInGroup, flaggedBy, useLiteStandards } from "./LiteStandards.jsx";
import { investigateLiteSession, readLiteChats, writeLiteChats, LITE_INVESTIGATE_TOOLS } from "../../lib/triageLiteChat.js";
import { getSuggestContext, openStandardSuggestions, setSuggestContext, subscribeSuggestContext } from "../../lib/standardSuggest.js";
import { missedFlagOf, openMissedFlag, openStandardTuning } from "../../lib/standardTune.js";
import { writeUrlParam } from "../../lib/urlParams.js";
import { TURN_FOCUS_PARAM, formatTurnFocus, parseTurnFocusMessage } from "../../lib/traceTurnFocus.js";
import { binFindings, fetchSessionTurns, findingSpan, recordMessageAt, TIMELINE_SESSIONS } from "../../lib/triageTimeline.js";
import { dayLabelFormatter, fullTimestampFormatter, timeLabelFormatter, parseServerTimestamp } from "../../lib/traceUtils.js";
import { SEVERITY_COLOR } from "../../lib/traceReview.js";
import { reportLabel } from "../../lib/traceReviewReports.js";
import {
  fetchScopeReviewFindings, fetchEscalatedReviewFindings, buildConduct, CONDUCT_FINDINGS_CEILING,
  REVIEW_SESSION_CAP, SESSION_OFFSET_CEILING, SESSION_SORT_OPTIONS,
  reviewsWindow, fetchScopeReviewSessions, fetchScopeReviewTotals, mergeSessionPages, buildSessionRows, sessionPaging,
  sessionsSummaryParts, sessionTimeline, filterSessions, sortSessions, fetchSessionFindings, REVIEW_FINDINGS_LIMIT,
  fetchSessionDropCounts, fetchSessionDroppedFindings, DROPPED_FINDINGS_LIMIT,
  fetchReviewFlow,
} from "../../lib/scopeReviews.js";
import { buildReviewFlow } from "../../lib/reviewFlow.js";
import ReviewFlow from "./ReviewFlow.jsx";
import InvestigateTab from "./InvestigateTab.jsx";
import { CaseSessions, SessionCard, machineOf, useSessionIdentity } from "./reviewSessions.jsx";

// Reviews SCOPE lens -- available at every scope as a toggle beside the
// overview: which conversations in this slice of traffic has the trace-review
// pipeline flagged, and what did it find in each?
//
// The grain is the SESSION, as the trace log's default is: one row per
// conversation that has had a finding land on it, newest review first,
// carrying the session's own summary over a line of review facts, and a
// timeline of its flagged reviews on the session's clock. A click opens a row
// and closes it again, the way the Triage tab's rows open: beneath it, where
// the session's findings cite on its turns (numbered the way the lanes number
// them) and the findings as a numbered list, each opening to what the judge
// quoted. The session itself opens through an explicit link, the same door a
// Sessions row opens, where the lanes carry the same reports and findings on
// the turn axis.
//
// Above the tabs, what became of the range's findings: what the reviews
// wrote, what the pipeline kept, what Triage checked of it and what the
// standard in use made of that, as one flow (ReviewFlow), so the eye lands on
// how far the narrowing has got before it reads any one list.
//
// The chrome is the trace log's, copied rather than shared: the tab head, the
// search and sort row pinned with the column header, the summary line, the
// table with the log's "load more" footer -- so the lens reads as the same
// surface, and the log's filtering tools have a place to land here later.
// Sessions page by the aggregate's offset; the true count comes from one
// un-grouped aggregate, so the summary line never mistakes a page for the
// whole.
//
// Scope resolves through SESSIONS: the sessions the overview would list for
// this scope, then the flagged reports among them (lib/scopeReviews), so every
// dim the sidebar can drill is covered by one mechanism. The tenant root reads
// the reports directly.
const SENSITIVE_NOTE = "Trace summaries and finding positions require permission to read sensitive trace data.";

// Conduct: the one column added to the log's own. The session's dominant
// habit, named the way the scope's other sessions name it, with how often it
// recurred in this session.
const sessionColumns = (showEndpoint) => [
  { id: "open", label: "", width: 14 },
  { id: "time", label: "Latest", width: 72 },
  { id: "session", label: "Trace", width: 200, flex: true },
  { id: "conduct", label: "Conduct", width: 190 },
  ...(showEndpoint ? [{ id: "endpoint", label: "Endpoint", width: 80 }] : []),
  { id: "timeline", label: "Timeline", width: 150 },
];

// The scope's session ids, resolved the way the trace log resolves them: the
// root reads directly; a nested scope lists its sessions first. Shared by the
// list and the graph so both read the same population.
function useScopeSessionIds({ scopePath, range, steer, sensitive }) {
  const direct = scopePath.length === 0 && !steer;
  const sessionQuery = useQuery("getSessionLog", {
    scopePath, range, limit: REVIEW_SESSION_CAP, sort: "newest", canReadSensitivePrompts: sensitive,
    ...(steer ? { query: steer } : {}),
  }, { staleMs: Infinity, enabled: !direct });
  // While a new search or scope is read, useQuery still holds the rows the
  // last one found, which are not its sessions: nothing reads them for it.
  const sessionRows = direct || sessionQuery.previousParams ? null : (Array.isArray(sessionQuery.data) ? sessionQuery.data : null);
  const sessionsReady = direct || sessionRows != null;
  const sessionsById = useMemo(() => {
    const map = new Map();
    for (const row of sessionRows || []) if (row?.sessionId) map.set(String(row.sessionId).toLowerCase(), row);
    return map;
  }, [sessionRows]);
  const sessionIds = useMemo(() => [...sessionsById.keys()], [sessionsById]);
  const unsupported = sessionRows?.unsupportedScope?.length ? sessionRows.unsupportedScope : null;
  const rangeKey = `${range?.from ?? ""}-${range?.to ?? ""}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const window = useMemo(() => reviewsWindow(range), [rangeKey]);
  return {
    direct,
    sessionIds,
    idsKey: direct ? "*" : sessionIds.join(","),
    sessionsById,
    sessionsReady,
    // More sessions in scope than the read resolved: the list covers a
    // sample and the summary line has to say so.
    sessionsCapped: !direct && sessionRows != null && Number(sessionRows.total) > sessionIds.length,
    unsupported,
    unsupportedKey: unsupported ? unsupported.join(",") : "",
    denied: !direct && sessionQuery.denied,
    // A query being read again still holds its last error.
    error: !direct && !sessionQuery.loading ? sessionQuery.error : null,
    window,
  };
}

// The sessions with findings for the current scope and range: the true count
// and the first page together, further pages on request. Re-read on an
// explicit change only -- scope, range, steer, or the sensitive-read posture
// -- never on a remount.
function useScopeReviewSessions({ ids, sensitive, conduct = null }) {
  const { direct, sessionIds, idsKey, sessionsById, sessionsReady, unsupported, unsupportedKey, denied, error: sessionsError, window } = ids;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scoped = useMemo(() => (direct ? null : sessionIds), [direct, idsKey]);
  const EMPTY = { status: "idle", pages: [], totals: null, error: null, loadingMore: false, moreError: null };
  const [state, setState] = useState(EMPTY);
  const controllerRef = useRef(null);
  useEffect(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    if (!sessionsReady) {
      setState({ ...EMPTY, status: denied ? "denied" : sessionsError ? "error" : "loading", error: sessionsError || null });
      return undefined;
    }
    if (unsupported) {
      setState({ ...EMPTY, status: "unsupported" });
      return undefined;
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    setState({ ...EMPTY, status: "loading" });
    const scope = { sessionIds: scoped, fromMs: window.fromMs, toMs: window.toMs, signal: controller.signal };
    Promise.all([
      fetchScopeReviewTotals(scope),
      fetchScopeReviewSessions({ ...scope, sensitive, offset: 0 }),
    ]).then(([totals, page]) => {
      if (controller.signal.aborted) return;
      setState({ ...EMPTY, status: "done", pages: [page], totals });
    }, (error) => {
      if (controller.signal.aborted) return;
      setState({ ...EMPTY, status: "error", error });
    });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direct, idsKey, sessionsReady, denied, sessionsError, unsupportedKey, window.fromMs, window.toMs, sensitive]);

  const merged = useMemo(() => mergeSessionPages(state.pages), [state.pages]);
  const result = useMemo(
    () => (state.status === "done" ? buildSessionRows({ ...merged, sessionsById, conduct }) : null),
    [state.status, merged, sessionsById, conduct],
  );
  const loaded = merged.aggregates.length;
  const lastPage = state.pages[state.pages.length - 1];
  const paging = sessionPaging({ loaded, total: state.totals?.sessions ?? 0, lastPageSize: lastPage?.aggregates?.length ?? 0, ceiling: SESSION_OFFSET_CEILING });
  const { remaining, atCeiling } = paging;
  const hasMore = state.status === "done" && paging.hasMore;
  const loadMore = useCallback(() => {
    if (!hasMore || state.loadingMore) return;
    const controller = controllerRef.current;
    if (!controller) return;
    setState((cur) => ({ ...cur, loadingMore: true, moreError: null }));
    fetchScopeReviewSessions({
      sessionIds: scoped, fromMs: window.fromMs, toMs: window.toMs, sensitive, offset: loaded, signal: controller.signal,
    }).then((page) => {
      if (controller.signal.aborted) return;
      setState((cur) => ({ ...cur, pages: [...cur.pages, page], loadingMore: false, moreError: null }));
    }, (error) => {
      if (controller.signal.aborted) return;
      console.warn("[reviews] next page unavailable:", error?.message || error);
      setState((cur) => ({ ...cur, loadingMore: false, moreError: error }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, state.loadingMore, direct, idsKey, window.fromMs, window.toMs, sensitive, loaded]);

  return { ...state, result, totals: state.totals, hasMore, loadMore, loaded, remaining, atCeiling, direct, sessionCount: sessionIds.length, window };
}

// The scope's conduct pool: the newest findings in the scope and range, one
// step at first, further steps on request up to the ceiling; or, with
// `escalated` ({ keepIds }), only what a standard met in the range and the
// findings keepIds names, whole. The types and each session's dominant
// conduct come out of the pool. Sensitive read only: without it there is no
// pool and the list says so.
function useScopeConduct({ ids, sensitive, limit, escalated = null }) {
  const { direct, sessionIds, idsKey, sessionsReady, unsupported, unsupportedKey, denied, error: sessionsError, window } = ids;
  const EMPTY = { status: "idle", rows: [], clipped: false, error: null, loadingMore: false, moreError: null };
  const [state, setState] = useState(EMPTY);
  const controllerRef = useRef(null);
  useEffect(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    if (!sensitive) { setState({ ...EMPTY, status: "hidden" }); return undefined; }
    if (!sessionsReady || unsupported || denied || (escalated && !escalated.keepIds)) {
      setState({ ...EMPTY, status: unsupported ? "unsupported" : denied ? "denied" : sessionsError ? "error" : "loading", error: sessionsError || null });
      return undefined;
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    setState({ ...EMPTY, status: "loading" });
    const read = escalated
      ? fetchEscalatedReviewFindings({ sessionIds: direct ? null : sessionIds, fromMs: window.fromMs, toMs: window.toMs, keepIds: escalated.keepIds, signal: controller.signal })
      : fetchScopeReviewFindings({ sessionIds: direct ? null : sessionIds, fromMs: window.fromMs, toMs: window.toMs, ...(limit ? { limit } : {}), signal: controller.signal });
    read.then((step) => {
      if (controller.signal.aborted) return;
      // With the sessions it was read for, so a reader can tell them from the next.
      setState({ ...EMPTY, status: "done", rows: step.rows, clipped: step.clipped, idsKey });
    }, (error) => {
      if (controller.signal.aborted) return;
      console.warn("[reviews] conduct unavailable:", error?.message || error);
      setState({ ...EMPTY, status: "error", error });
    });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direct, idsKey, sessionsReady, denied, sessionsError, unsupportedKey, window.fromMs, window.toMs, sensitive, limit, escalated ? escalated.keepIds?.join(",") ?? "waiting" : null]);
  const atCeiling = escalated ? state.clipped : state.rows.length >= CONDUCT_FINDINGS_CEILING;
  const hasMore = !escalated && state.status === "done" && state.clipped && !atCeiling;
  const loadMore = useCallback(() => {
    if (!hasMore || state.loadingMore) return;
    const controller = controllerRef.current;
    if (!controller) return;
    setState((cur) => ({ ...cur, loadingMore: true, moreError: null }));
    fetchScopeReviewFindings({
      sessionIds: direct ? null : sessionIds, fromMs: window.fromMs, toMs: window.toMs, offset: state.rows.length, signal: controller.signal,
    }).then((step) => {
      if (controller.signal.aborted) return;
      setState((cur) => {
        const seen = new Set(cur.rows.map((r) => String(r.finding_id)));
        const rows = cur.rows.concat(step.rows.filter((r) => !seen.has(String(r.finding_id))));
        return { ...cur, rows, clipped: step.clipped, loadingMore: false, moreError: null };
      });
    }, (error) => {
      if (controller.signal.aborted) return;
      console.warn("[reviews] more conduct unavailable:", error?.message || error);
      setState((cur) => ({ ...cur, loadingMore: false, moreError: error }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, state.loadingMore, state.rows.length, direct, idsKey, window.fromMs, window.toMs]);
  const conduct = useMemo(() => buildConduct({ rows: state.rows, clipped: state.clipped }), [state.rows, state.clipped]);
  return { ...state, conduct, hasMore, atCeiling, loadMore };
}

// What the range's reviews wrote and what the pipeline kept of it, over the
// population the list reads: the report headers' sums, one aggregate, read
// once per scope and range like the list. When Triage first checked a finding
// inside the range, the sums start there (`since`), so every step of the flow
// counts the same findings; unread, the flow keeps the whole range.
function useReviewFlowSums({ ids }) {
  const { direct, sessionIds, idsKey, sessionsReady, unsupported, window } = ids;
  const [state, setState] = useState({ status: "idle", sums: null, error: null, since: null });
  useEffect(() => {
    if (!sessionsReady || unsupported) {
      setState({ status: "idle", sums: null, error: null, since: null });
      return undefined;
    }
    const controller = new AbortController();
    setState({ status: "loading", sums: null, error: null, since: null });
    // A scope with no sessions has no flow to start anywhere.
    const noSessions = !direct && Array.isArray(sessionIds) && !sessionIds.length;
    (noSessions ? Promise.resolve(null) : fetchTriageStart({ fromMs: window.fromMs, signal: controller.signal }))
      .catch((error) => {
        if (!controller.signal.aborted) console.warn("[reviews] when Triage started is unavailable:", error?.message || error);
        return null;
      })
      .then((start) => {
        if (controller.signal.aborted) return;
        const since = start != null && start > window.fromMs && start < window.toMs ? start : null;
        return fetchReviewFlow({ sessionIds: direct ? null : sessionIds, fromMs: since ?? window.fromMs, toMs: window.toMs, signal: controller.signal })
          .then((sums) => {
            if (controller.signal.aborted) return;
            setState({ status: "done", sums, error: null, since });
          }, (error) => {
            if (controller.signal.aborted) return;
            console.warn("[reviews] flow totals unavailable:", error?.message || error);
            setState({ status: "error", sums: null, error, since });
          });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direct, idsKey, sessionsReady, !!unsupported, window.fromMs, window.toMs]);
  return state;
}

// An opened session's findings, read once when it first opens and kept for
// the lens's life; a new list drops them all. The findings land first, then
// where their citations sit on the session's turns, numbered the way the
// lanes number them. Each session is asked for once, tracked in a ref rather
// than derived from the answers, so marking a session as loading does not
// re-key the effect and abort the very read it started. Beside the kept read,
// the session's report headers say how many findings its reviews wrote and
// did not keep; the two land together, so the line for those lands with the
// kept findings rather than after them, and then, when there are any, the
// findings themselves and where their quotes sit. A role without sensitive
// read never asks -- titles, quotes and message ids are prose and evidence
// locations.
function useSessionFindings({ result, expanded, window, sensitive }) {
  const [entries, setEntries] = useState(() => new Map());
  const requested = useRef(new Set());
  const controllerRef = useRef(null);
  useEffect(() => {
    requested.current = new Set();
    controllerRef.current?.abort();
    controllerRef.current = null;
    setEntries(new Map());
  }, [result]);
  useEffect(() => {
    if (!result || !sensitive) return;
    for (const id of expanded) {
      if (requested.current.has(id)) continue;
      const session = result.sessions.find((s) => s.id === id);
      if (!session) continue;
      requested.current.add(id);
      const controller = controllerRef.current || (controllerRef.current = new AbortController());
      const put = (entry) => setEntries((cur) => new Map(cur).set(id, { ...cur.get(id), ...entry }));
      const putDropped = (d) => setEntries((cur) => new Map(cur).set(id, { ...cur.get(id), dropped: { ...cur.get(id)?.dropped, ...d } }));
      const range = { fromMs: window.fromMs, toMs: window.toMs, signal: controller.signal };
      const readDropped = (counts) => fetchSessionDroppedFindings({ session: { id, reviews: counts.reviews }, ...range }).then((read) => {
        if (controller.signal.aborted) return;
        const messageIds = read.findings.flatMap((f) => f.citations.map((ct) => ct.messageId)).filter(Boolean);
        putDropped({ status: "done", findings: read.findings, expired: read.expired, truncated: counts.truncated || read.truncated, placing: messageIds.length > 0 });
        if (!messageIds.length) return;
        fetchSessionTurns({ session: id, messageIds, signal: controller.signal }).then(
          (placed) => { if (!controller.signal.aborted) putDropped({ turns: placed.turns, placing: false }); },
          (error) => {
            if (controller.signal.aborted) return;
            console.warn("[reviews] turn positions unavailable:", error?.message || error);
            putDropped({ placing: false });
          },
        );
      }, (error) => {
        if (controller.signal.aborted) return;
        console.warn("[reviews] findings not kept unavailable:", error?.message || error);
        putDropped({ status: "error", error });
      });
      const drops = fetchSessionDropCounts({ session, ...range }).then(
        (counts) => ({ ...counts, status: counts.count > 0 ? "loading" : "none" }),
        (error) => {
          if (!controller.signal.aborted) console.warn("[reviews] findings not kept unavailable:", error?.message || error);
          return { status: "error", count: 0, reviews: [], truncated: false, error };
        },
      );
      put({ status: "loading", findings: [], truncated: false, timeline: null, dropped: null });
      Promise.all([fetchSessionFindings({ session, ...range }), drops]).then(([{ findings, truncated }, counts]) => {
        if (controller.signal.aborted) return;
        const bare = { session: id, findings, total: null, turns: new Map(), truncated: false };
        put({
          status: "done", findings, truncated, timeline: { status: "loading", sessions: [{ ...bare, status: "loading" }], rest: 0 },
          dropped: { ...counts, findings: [], expired: 0, turns: null, placing: false },
        });
        if (counts.status === "loading") readDropped(counts);
        const settle = (strip) => { if (!controller.signal.aborted) put({ timeline: { status: strip.status, sessions: [strip], rest: 0 } }); };
        fetchSessionTurns({ session: id, messageIds: findings.flatMap((f) => f.citations.map((ct) => ct.messageId)), signal: controller.signal })
          .then((read) => settle({ ...bare, ...read, status: "done" }), (error) => {
            if (!controller.signal.aborted) console.warn("[reviews] turn positions unavailable:", error?.message || error);
            settle({ ...bare, status: "error" });
          });
      }, (error) => {
        if (controller.signal.aborted) return;
        console.warn("[reviews] findings unavailable:", error?.message || error);
        put({ status: "error", findings: [], truncated: false, timeline: null, error });
      });
    }
  }, [result, expanded, sensitive, window.fromMs, window.toMs]);
  useEffect(() => () => controllerRef.current?.abort(), []);
  return entries;
}

// The hover card behind a timeline mark: a header line, the words, the
// reasoning beneath them, a note.
function MarkCard({ header, body, detail = null, note = null, tone = "calm" }) {
  return (
    <div className="grid max-w-[360px] gap-[4px] text-left normal-case tracking-normal">
      <span className="text-[11px] leading-[1.3]" style={{ color: tone === "calm" ? "var(--ink)" : SEVERITY_COLOR[tone] }}>{header}</span>
      {body ? <span className="text-[11px] leading-[1.4] text-[color:var(--ink)] [overflow-wrap:anywhere]">{body}</span> : null}
      {detail ? <span className="text-[11px] leading-[1.4] text-[color:var(--muted)] [overflow-wrap:anywhere]">{detail}</span> : null}
      {note ? <span className="text-[10px] leading-[1.3] text-[color:var(--muted)]">{note}</span> : null}
    </div>
  );
}

// A mark's left edge on a strip: `position` across the strip, shifted by its
// slot so marks sharing a turn stand side by side. The whole offset is clamped
// to the strip, so a slotted mark at the last turn is drawn at the edge rather
// than clipped away by it.
export function stripMarkLeft(position, slot = 0) {
  return `min(calc(${(position * 100).toFixed(2)}% + ${slot * 4}px), calc(100% - 3px))`;
}

// A mark on a strip: 3px wide, at stripMarkLeft.
function StripMark({ position, slot = 0, tone, label, ...attrs }) {
  return (
    <HoverTooltip label={label} placement="top" wide>
      <span
        {...attrs}
        data-mark-left={stripMarkLeft(position, slot)}
        className="absolute top-0 block h-full w-[3px]"
        style={{
          left: stripMarkLeft(position, slot),
          backgroundColor: tone === "calm" ? "var(--ink)" : SEVERITY_COLOR[tone],
        }}
      />
    </HoverTooltip>
  );
}

// A session's flagged reviews on its own clock: first review at the left,
// latest at the right, one mark per review toned like its worst rung. The
// strip fills its column; the facts line beside the summary already counts
// the reviews. Hovering a mark says when it was reviewed, what it raised, and
// what the judge wrote.
function SessionTimelineStrip({ session, sensitive }) {
  const { marks } = sessionTimeline(session);
  return (
    <div data-session-timeline={marks.length} className="relative h-[12px] w-full min-w-0 overflow-hidden rounded-[1px] bg-[color:var(--line-soft)]">
      {marks.map((m) => {
        const r = m.review;
        const rungs = [r.worstBehaviour, r.worstContent].filter(Boolean).join(" \u00b7 ");
        const header = `reviewed ${fullTimestampFormatter.format(new Date(m.at))} \u00b7 ${r.findingCount} finding${r.findingCount === 1 ? "" : "s"}${rungs ? ` \u00b7 ${rungs}` : ""}`;
        const body = sensitive ? (r.summary || reportLabel(r)) : null;
        const note = [r.partial ? "partial window" : null, r.targetItemCount ? `${r.targetItemCount} turns reviewed` : null].filter(Boolean).join(" \u00b7 ") || null;
        return <StripMark key={m.id} data-session-mark={m.id} position={m.position} tone={m.tone} label={<MarkCard header={header} body={body} note={note} tone={m.tone} />} />;
      })}
    </div>
  );
}

const FACTS = "truncate text-[9px] leading-[1.2]";

// The session's dominant conduct, and how often it recurred here. Named the
// way the scope's pool groups it, so two sessions with one habit read the
// same; the hover gives the whole name and the words the judge used for it in
// this session.
function ConductCell({ session, sensitive }) {
  const c = session.conduct;
  if (!sensitive) return <span className="text-[10px] leading-[1.12] text-[color:var(--muted-soft)]">hidden</span>;
  if (!c) return <span className="text-[10px] leading-[1.12] text-[color:var(--muted-soft)]">{"\u2014"}</span>;
  const here = (c.titles || []).filter((t) => t !== c.name);
  return (
    <span className="flex min-w-0 items-baseline gap-x-[5px] text-[11px] leading-[1.12]" data-session-conduct="">
      <OverflowTooltip
        text={c.name}
        always={here.length > 0}
        tooltipContent={(
          <span className="grid gap-[3px]">
            <span>{c.name}</span>
            {here.length ? <span className="text-[color:var(--muted)]">in this trace: {here.join(" \u00b7 ")}</span> : null}
          </span>
        )}
        className="min-w-0 truncate text-[color:var(--ink)]"
      />
      {c.count > 1 ? <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted)]">{"\u00d7"}{c.count}</span> : null}
    </span>
  );
}

// On the head of an opened session: its case in the Investigate tab, opened
// and dug.
function InvestigateChip({ onInvestigate }) {
  return (
    <button type="button" data-reviews-investigate="" onClick={(e) => { e.stopPropagation(); onInvestigate(); }} className={ACTION_CHIP}>
      Investigate
    </button>
  );
}

// Keep review cases on the panel surface. The case file is read, not clicked,
// so it keeps the panel under the pointer too.
const PANEL_ROW = "[&]:bg-[color:var(--panel)]";
const PANEL_STILL_ROW = `${PANEL_ROW} [&:hover]:bg-[color:var(--panel)]`;
const plural = (n, word) => `${word}${n === 1 ? "" : "s"}`;
const flipped = (set, key) => {
  const next = new Set(set);
  if (next.has(key)) next.delete(key); else next.add(key);
  return next;
};
const openRow = (set, key) => (set.has(key) ? set : new Set(set).add(key));
const scrollToFinding = (wrapRef, findingId) => requestAnimationFrame(() => {
  wrapRef.current?.querySelector(`[data-triage-finding="${escapeAttr(findingId)}"]`)?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
});

// The findings pool both agent tabs read. When it has nothing to give them
// the tab says why in place of its list, and while it first reads, that it is
// reading; when a step back failed, and when it stops at its ceiling, a line
// under the window says so.
const POOL_READING = "Reading the scope\u2019s findings\u2026";
function poolBlocker(pool, caps, scopeLabel) {
  if (pool?.status === "hidden") return "Triage and Investigate need permission to read sensitive trace data.";
  if (pool?.status === "denied") return "You do not have permission to read reviews for this scope.";
  if (pool?.status === "unsupported") return `Findings cannot be read for ${scopeLabel}.`;
  if (pool?.status === "error") return analyticsErrorCopy(pool.error, caps)?.message || "The findings could not be read. Reload to try again.";
  return null;
}
function PoolBlocked({ text, error = false }) {
  return (
    <div role={error ? "alert" : "status"} data-pool-blocked="" className="grid gap-[4px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px]">
      <strong className="text-[13px] leading-[1.08] font-medium">{text}</strong>
    </div>
  );
}
function PoolLine({ pool }) {
  if (pool?.moreError) return <span data-pool-line="error" role="alert" className={NOTE_CLASS}>Older results could not be loaded. Select Load older to retry.</span>;
  if (pool?.atCeiling && pool.clipped) {
    return <span data-pool-line="ceiling" className={NOTE_CLASS}>The pool stops at {CONDUCT_FINDINGS_CEILING.toLocaleString()} findings. Narrow the scope or the date range to reach the rest.</span>;
  }
  return null;
}

// A finding in its case's numbered list: one line, the number the timeline
// names it by, its severity and title, and where, at what turns and when it
// was reviewed. Opening it shows what the judge quoted. A `quiet` line is one
// the review did not keep: its words and rating recede behind the kept ones.
function FindingLine({ f, c, index, open, span, placing, note = null, quiet = false, onToggle, onHover, inset = CASE_INSET }) {
  const ownWhy = c.count > 1 && f.why && f.why !== c.why ? f.why : "";
  const at = f.completedAt ? parseServerTimestamp(f.completedAt) : NaN;
  const rowId = `${c.id}/${f.id}`;
  return (
    <span
      data-triage-finding={f.id}
      onMouseEnter={() => onHover(f.id)}
      onMouseLeave={() => onHover(null)}
      className="flex min-w-0 items-center gap-x-[8px]"
      style={{ paddingLeft: inset }}
    >
      <Disclosure data-triage-finding-toggle="" open={open} label={open ? "Hide what the judge quoted" : "Show what the judge quoted"} onToggle={() => onToggle(rowId)} />
      <span data-triage-index="" className="w-[22px] shrink-0 text-right text-[9px] leading-[15px] tabular-nums text-[color:var(--muted-soft)]">{index}</span>
      <SeverityWord severity={f.severity} quiet={quiet} />
      <span className="flex min-w-0 flex-1 items-center gap-x-[8px]">
        <span className={`min-w-0 truncate text-[11px] leading-[1.35] ${quiet ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>
          {f.title}
          {ownWhy ? <span className="text-[color:var(--muted)]">{" \u00b7 "}{ownWhy}</span> : null}
          {note ? <span data-triage-finding-note="" className="text-[color:var(--muted)]">{" \u00b7 "}{note}</span> : null}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-x-[10px]">
        <span data-triage-turns="" className="w-[96px] shrink-0 text-right font-mono text-[10px] leading-[15px] tabular-nums text-[color:var(--muted)]">
          {turnSpanLabel(span) || (placing ? "\u2026" : "\u2014")}
        </span>
        <span className="w-[96px] shrink-0 text-right text-[9px] leading-[15px] tabular-nums text-[color:var(--muted-soft)]">
          {Number.isFinite(at) ? `${dayLabelFormatter.format(at)} ${timeLabelFormatter.format(at)}` : "unknown"}
        </span>
      </span>
    </span>
  );
}

// What Triage made of an opened finding that no standard flagged: its verdict,
// the standard that came closest and why it said no, and, where a chat can
// open, a way to tune the standards to catch it.
function MissedFlag({ findingId }) {
  const canAsk = useSyncExternalStore(subscribeTriageChatHost, () => typeof getTriageChatHost()?.ask === "function", () => false);
  const ctx = useSyncExternalStore(subscribeSuggestContext, getSuggestContext, () => null);
  const missed = ctx?.sensitive ? missedFlagOf(ctx, findingId) : null;
  if (!missed) return null;
  const { verdict, closest } = missed;
  const tune = (ev) => {
    ev.stopPropagation();
    try { openMissedFlag({ host: getTriageChatHost(), findingId }); } catch { /* shown only where a chat can open */ }
  };
  return (
    <>
      <span className={`${FILE_LABEL} col-start-2`}>Triage</span>
      <span data-triage-missed={findingId} className="col-start-2 pb-[4px] text-[11px] leading-[1.45] text-[color:var(--muted)]">
        <span className="text-[color:var(--ink)]">{liteVerdictLabel(verdict)}; no standard flagged it.</span>
        {closest ? <> {standardLabel(closest.item)} said yes once, then no{closest.why ? `: ${closest.why}` : "."}</> : null}
        {canAsk ? (
          <>
            {" "}
            <button
              type="button"
              data-triage-missed-tune={findingId}
              onClick={tune}
              title="Opens a chat that tunes the standards so they flag this finding"
              className="cursor-pointer border-0 bg-transparent p-0 text-[11px] leading-[1.45] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]"
            >
              Should this have been flagged? Tune it
            </button>
          </>
        ) : null}
      </span>
    </>
  );
}

// An opened finding: why the judge raised it where the read carries that,
// each passage it quoted, at the turn it quoted it from, and the way into the
// session.
const QUOTE_SHOWN = 600;
function FindingQuotes({ f, turns, onOpen, reason = null, inset = CASE_INSET }) {
  const cites = f.citations?.length ? f.citations : (f.quotes || []).map((quote) => ({ quote, messageId: null }));
  const lead = cites.find((ct) => ct.messageId) || null;
  const leadTurn = lead ? turns?.get(lead.messageId) ?? null : null;
  // The turn sits under the finding's number and severity, the quote under its title.
  return (
    <div data-triage-cite="" className="grid cursor-auto gap-x-[8px] gap-y-[4px] pt-[2px] pb-[8px] [grid-template-columns:64px_minmax(0,1fr)]" style={{ paddingLeft: inset + 22 }}>
      {reason ? (
        <>
          <span className={`${FILE_LABEL} col-start-2`}>Why it was not kept</span>
          <span data-reviews-dropped-reason="" className="col-start-2 pb-[4px] text-[11px] leading-[1.45] text-[color:var(--ink)]">{reason}</span>
        </>
      ) : null}
      {f.rationale ? (
        <>
          <span className={`${FILE_LABEL} col-start-2`}>Why it was raised</span>
          <span data-triage-rationale="" className="col-start-2 pb-[4px] text-[11px] leading-[1.45] text-[color:var(--ink)]">{f.rationale}</span>
        </>
      ) : null}
      {reason ? null : <MissedFlag findingId={f.id} />}
      <span className={`${FILE_LABEL} col-start-2`}>Quoted by the judge</span>
      {cites.map((ct, i) => {
        const turn = ct.messageId ? turns?.get(ct.messageId) ?? null : null;
        return (
          <div key={i} data-triage-quote="" className="col-span-2 grid gap-x-[8px] border-t border-[color:var(--line-soft)] pt-[5px] first-of-type:border-t-0 [grid-template-columns:64px_minmax(0,1fr)]">
            <span className="text-right font-mono text-[10px] leading-[1.6] tabular-nums text-[color:var(--muted-soft)]">
              {turn != null ? (
                <button
                  type="button"
                  data-triage-cite-turn={turn}
                  onClick={(ev) => { ev.stopPropagation(); onOpen?.({ session: f.session, turn, message: ct.messageId }); }}
                  className="cursor-pointer p-0 text-[color:var(--ink)] underline decoration-dotted underline-offset-2"
                >
                  T{turn}
                </button>
              ) : "\u2014"}
            </span>
            <span className="font-mono text-[10px] leading-[1.5] whitespace-pre-wrap text-[color:var(--ink)] [overflow-wrap:anywhere]">
              {ct.quote ? ct.quote.slice(0, QUOTE_SHOWN) : <span className="font-sans text-[color:var(--muted)]">No quote was kept for this citation.</span>}
            </span>
          </div>
        );
      })}
      <span className="col-start-2 pt-[2px]">
        <button
          type="button"
          data-triage-open-session=""
          onClick={(ev) => { ev.stopPropagation(); onOpen?.({ session: f.session, turn: leadTurn, message: lead?.messageId || null }); }}
          className={ACTION_CHIP}
        >
          Open the trace
        </button>
      </span>
    </div>
  );
}

const BAR_MAX = 44;
const BAR_MAX_COMPACT = 22;
const binKey = (caseId, session, bin) => `${caseId}|${session}|${bin}`;
const binMid = (bin) => Math.round((bin.from + bin.to) / 2);

// While the findings are placed, every bin stands as a faint bar as tall as a
// landed bar can grow, lighter than any landed bar so it cannot pass for one,
// under a soft band of light (.timeline-placing in styles.css). A mask cuts
// the band into the stretches and gaps the landed bars take. The words stay:
// above the bars on a strip tall enough, for a screen reader otherwise.
const BIN = `calc(100% / ${TIMELINE_BINS})`;
const PLACING_MASK = `repeating-linear-gradient(to right, transparent 0, transparent 1px, #000 1px, #000 calc(${BIN} - 1px), transparent calc(${BIN} - 1px), transparent ${BIN})`;
function PlacingBars({ compact, caption }) {
  return (
    <>
      <span
        data-triage-placing=""
        className="timeline-placing absolute inset-x-0 bottom-0"
        style={{ height: compact ? BAR_MAX_COMPACT : BAR_MAX, maskImage: PLACING_MASK, WebkitMaskImage: PLACING_MASK }}
        aria-hidden="true"
      />
      <span className={compact ? "sr-only" : "absolute inset-x-0 top-0 truncate text-center text-[9px] leading-[12px] text-[color:var(--muted)]"}>{caption}</span>
    </>
  );
}

// What a session is called: its summary, the way the Sessions tab names it,
// and its short id only where no summary was kept.
const sessionName = (session, labels) => labels?.get(session) || `Trace ${String(session).slice(0, 8)}`;

// One session's bars. A finding hovered in the list lightens the bars it
// cites; an opened finding darkens them and names itself above them by its
// number; the picked bar is ringed while its findings show beneath it. A case
// over several sessions draws each shorter, beside its name.
function SessionStrip({ caseId, s, compact, picked, onPick, highlight, openFindings, indexOf }) {
  const status = s.status;
  const ready = status === "done" && s.total > 0;
  const binned = useMemo(() => (ready ? binFindings(s.findings, s.turns, s.total, TIMELINE_BINS) : []), [ready, s.findings, s.turns, s.total]);
  const tallest = Math.max(1, ...binned.map((b) => b.findings.length));
  const placed = ready ? s.findings.filter((f) => findingSpan(f, s.turns)).length : 0;
  const barMax = compact ? BAR_MAX_COMPACT : BAR_MAX;
  let empty = null;
  if (status === "loading") empty = "Placing the findings on the trace's turns";
  else if (status === "error") empty = "The turns these findings cite could not be read.";
  else if (!ready) empty = "This trace's length could not be read.";
  else if (!placed) empty = s.truncated ? "These findings cite turns further back than the timeline reads." : "None of these findings cite a turn the record holds in this window.";
  return (
    <div data-triage-strip={s.session} className="grid min-w-0 gap-[1px]">
      <div className={`relative ${compact ? "h-[30px]" : "h-[58px]"}`}>
        <span className="absolute inset-x-0 bottom-0 h-[1px]" style={{ backgroundImage: BASELINE }} aria-hidden="true" />
        {status === "loading" ? (
          <PlacingBars compact={compact} caption={empty} />
        ) : empty ? (
          <span className="absolute inset-x-0 bottom-[8px] truncate text-center text-[9px] text-[color:var(--muted-soft)]">{empty}</span>
        ) : binned.map((b) => {
          if (!b.findings.length) return null;
          const on = picked === b.index;
          const opened = b.findings.filter((f) => openFindings.has(f.id));
          const lit = !on && !opened.length && highlight && b.findings.some((f) => f.id === highlight);
          const tone = opened.length ? "bg-[color:var(--ink)]" : on || lit ? "bg-[color:var(--muted)]" : "bg-[color:var(--line-strong)] hover:bg-[color:var(--muted-soft)]";
          return (
            <span
              key={b.index}
              className="absolute bottom-0 flex h-full flex-col items-stretch justify-end px-[1px]"
              style={{ left: `${((b.index / TIMELINE_BINS) * 100).toFixed(3)}%`, width: `${(100 / TIMELINE_BINS).toFixed(3)}%` }}
            >
              {opened.length ? (
                <span data-triage-bin-label="" className="mb-[2px] truncate text-center font-mono text-[10px] leading-none text-[color:var(--ink)]">
                  {opened.map((f) => indexOf(f.id)).join(",")}
                </span>
              ) : null}
              <button
                type="button"
                data-triage-bin={b.index}
                data-triage-bin-key={binKey(caseId, s.session, b.index)}
                aria-pressed={on}
                aria-label={`${b.findings.length} ${b.findings.length === 1 ? "finding cites" : "findings cite"} turns ${b.from} to ${b.to}`}
                onClick={(e) => { e.stopPropagation(); onPick(on ? null : b.index); }}
                className={`w-full shrink-0 cursor-pointer rounded-t-[1px] border-0 p-0 transition-colors ${tone} ${on ? "outline outline-[1.5px] outline-offset-[2px] outline-[color:var(--ink)]" : ""}`}
                style={{ height: `${Math.max(3, Math.round((b.findings.length / tallest) * barMax))}px` }}
              />
            </span>
          );
        })}
      </div>
      {ready ? <TurnTicks total={s.total} /> : <span className="h-[12px]" aria-hidden="true" />}
    </div>
  );
}

// What a strip leaves out, said once and quietly: findings whose turns the
// record does not hold, and citations older than the pages the read reaches.
function stripCaveats(s) {
  if (s.status !== "done" || !(s.total > 0)) return [];
  const unplaced = s.findings.filter((f) => !findingSpan(f, s.turns)).length;
  const placed = unplaced < s.findings.length;
  return [
    unplaced && placed ? `${unplaced} not placed` : null,
    s.truncated && placed ? "older citations not read" : null,
  ].filter(Boolean);
}

// Where an opened case's findings cite, directly above its numbered findings:
// one session as one histogram, several as rows named by their summaries.
// `action` sits at the head's right edge; `note` closes it.
function CaseTimeline({ c, entry, labels, machineFor, picked, onPick, highlight, openFindings, indexOf, inset = CASE_INSET, action = null, note = null }) {
  const status = entry?.status || "loading";
  const sessions = entry?.sessions || [];
  const multi = sessions.length > 1 || c.sessionCount > 1;
  const drawn = sessions.some((s) => s.status === "done" && s.total > 0 && s.findings.some((f) => findingSpan(f, s.turns)));
  const strip = (s) => (
    <SessionStrip
      key={s.session}
      caseId={c.id}
      s={s}
      compact={multi}
      picked={picked?.session === s.session ? picked.bin : null}
      onPick={(bin) => onPick(bin == null ? null : { caseId: c.id, session: s.session, bin })}
      highlight={highlight}
      openFindings={openFindings}
      indexOf={indexOf}
    />
  );
  const loneCaveats = multi ? [] : sessions.flatMap(stripCaveats);
  return (
    <div data-triage-timeline={status} aria-busy={status === "loading" || undefined} className="grid cursor-auto gap-[6px] pt-[2px] pb-[6px]" style={{ paddingLeft: inset }}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-[12px] gap-y-[2px]">
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-[10px]">
          <span className={FILE_LABEL}>
            Where each finding&rsquo;s evidence sits{" \u00b7 "}{c.count.toLocaleString()} {plural(c.count, "finding")}
            {multi ? ` in ${c.sessionCount.toLocaleString()} traces` : ""}
          </span>
          {loneCaveats.length ? <span data-triage-strip-facts="" className="text-[9px] text-[color:var(--muted)]">{loneCaveats.join(" \u00b7 ")}</span> : null}
        </span>
        {drawn || action ? (
          <span className="flex items-baseline gap-x-[12px]">
            {drawn ? <span className="text-[9px] text-[color:var(--muted-soft)]">Pick a bar to see which findings cite there</span> : null}
            {action}
          </span>
        ) : null}
      </div>
      {multi ? (
        <div className="grid items-start gap-x-[14px] gap-y-[8px] [grid-template-columns:minmax(0,200px)_minmax(0,1fr)]">
          {sessions.flatMap((s) => {
            const caveats = stripCaveats(s);
            return [
              <div key={`${s.session}/name`} data-triage-strip-name={s.session} className="grid min-w-0 gap-[1px] pt-[6px]">
                <OverflowTooltip
                  text={sessionName(s.session, labels)}
                  always
                  tooltipContent={<SessionCard id={s.session} label={labels?.get(s.session) || null} machine={machineFor(s.session)} />}
                  className="min-w-0 truncate text-[11px] leading-[1.35] text-[color:var(--ink)]"
                />
                {machineFor(s.session) ? <span data-triage-machine="" className="truncate text-[9px] leading-[1.35] text-[color:var(--muted-soft)]">{machineFor(s.session).text}</span> : null}
                <span data-triage-strip-facts="" className="truncate text-[9px] leading-[1.35] text-[color:var(--muted)]">
                  {s.findings.length} {plural(s.findings.length, "finding")}
                  {s.status === "done" && s.total > 0 ? `${" \u00b7 "}${s.total.toLocaleString()} turns` : ""}
                  {caveats.length ? `${" \u00b7 "}${caveats.join(" \u00b7 ")}` : ""}
                </span>
              </div>,
              strip(s),
            ];
          })}
        </div>
      ) : sessions.map((s) => strip(s))}
      {entry?.rest ? (
        <span className="text-[9px] leading-[1.35] text-[color:var(--muted)]">
          {entry.rest.toLocaleString()} more {plural(entry.rest, "trace")} {entry.rest === 1 ? "holds" : "hold"} findings in this case; the {TIMELINE_SESSIONS} with the most are drawn.
        </span>
      ) : null}
      {note ? <span data-triage-timeline-note="" className="text-[9px] leading-[1.35] text-[color:var(--muted)]">{note}</span> : null}
    </div>
  );
}

// The findings a picked bar holds, in a card hung from the bar by its notch
// and laid over the list rather than pushing it down. Picking one opens it
// in the list, where its quotes and the way into the session are.
const POPOVER_WIDTH = 440;
function BinPopover({ innerRef, place, bin, indexOf, turns, onChoose, onClose }) {
  return (
    <div
      ref={innerRef}
      data-triage-bin-list={bin.index}
      role="dialog"
      aria-label={`Findings citing turns near T${binMid(bin)}`}
      onClick={(e) => e.stopPropagation()}
      className={`absolute z-30 max-w-full ${POPOVER_CARD}`}
      style={{ top: `${place.top}px`, left: `${place.left}px`, width: `${POPOVER_WIDTH}px` }}
    >
      <span
        className="absolute -top-[5px] h-[9px] w-[9px] rotate-45 border-t border-l border-[color:var(--line-strong)] bg-[color:var(--panel)]"
        style={{ left: `${place.notch - 4.5}px` }}
        aria-hidden="true"
      />
      <div className="flex items-baseline justify-between gap-x-[8px] px-[12px] pt-[9px] pb-[4px]">
        <span className={FILE_LABEL}>{bin.findings.length} {bin.findings.length === 1 ? "finding cites" : "findings cite"} turns near T{binMid(bin)}</span>
        <button type="button" aria-label="Close" onClick={onClose} className="cursor-pointer border-0 bg-transparent p-0 text-[11px] leading-none text-[color:var(--muted)] hover:text-[color:var(--ink)]">{"\u00d7"}</button>
      </div>
      <div className="grid max-h-[240px] overflow-y-auto px-[4px] pb-[4px]">
        {bin.findings.map((f) => {
          const here = (f.citations || []).map((ct) => turns?.get(ct.messageId)).filter((t) => t >= bin.from && t <= bin.to);
          const span = here.length ? { from: Math.min(...here), to: Math.max(...here) } : null;
          return (
            <button
              key={f.id}
              type="button"
              data-triage-bin-finding={f.id}
              onClick={() => onChoose(f.id)}
              className="grid cursor-pointer items-center gap-x-[8px] rounded-[6px] border-0 bg-transparent px-[8px] py-[5px] text-left hover:bg-[color-mix(in_srgb,var(--ink)_8%,transparent)] [grid-template-columns:22px_34px_minmax(0,1fr)_auto]"
            >
              <span className="text-right text-[9px] tabular-nums text-[color:var(--muted-soft)]">{indexOf(f.id)}</span>
              <SeverityWord severity={f.severity} />
              <span className="truncate text-[11px] leading-[1.35] text-[color:var(--ink)]">{f.title}</span>
              <span className="font-mono text-[10px] tabular-nums text-[color:var(--muted)]">{turnSpanLabel(span) || ""}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Where a picked bar's card hangs: under the bar, over the list, inside the
// list's own box so it scrolls with the rows. Measured after every render
// while a bar is picked, and again when the list's width changes.
const escapeAttr = (v) => String(v).replace(/["\\]/g, "\\$&");
function useBinPlacement(pickedBin, wrapRef) {
  const [place, setPlace] = useState(null);
  const [, remeasure] = useState(0);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const bar = pickedBin && wrap ? wrap.querySelector(`[data-triage-bin-key="${escapeAttr(binKey(pickedBin.caseId, pickedBin.session, pickedBin.bin))}"]`) : null;
    let next = null;
    if (bar) {
      const w = wrap.getBoundingClientRect();
      const b = bar.getBoundingClientRect();
      const width = Math.min(POPOVER_WIDTH, w.width || POPOVER_WIDTH);
      const centre = b.left + b.width / 2 - w.left;
      const left = Math.max(0, Math.min(centre - width / 2, (w.width || width) - width));
      // Below the bar's turn labels, the notch pointing back up at the bar.
      next = { top: Math.round(b.bottom - w.top + 20), left: Math.round(left), notch: Math.round(centre - left) };
    }
    setPlace((cur) => (cur && next && cur.top === next.top && cur.left === next.left && cur.notch === next.notch) || (!cur && !next) ? cur : next);
  });
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!pickedBin || !wrap || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => remeasure((n) => n + 1));
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [pickedBin, wrapRef]);
  return place;
}

// One picked bar across a list and the card it hangs: which bar, where its
// card sits, and what the bar holds. The card closes on a second pick, on
// Escape, and on a press anywhere outside it and the bars. `lookup` answers a
// picked bar with { bin, turns, order } or null; the caller keeps it stable.
function useBinCard(wrapRef, lookup) {
  const [pickedBin, setPickedBin] = useState(null);
  const popRef = useRef(null);
  const place = useBinPlacement(pickedBin, wrapRef);
  useEffect(() => {
    if (!pickedBin) return undefined;
    const onDown = (e) => {
      if (popRef.current?.contains(e.target) || e.target?.closest?.("[data-triage-bin]")) return;
      setPickedBin(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setPickedBin(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pickedBin]);
  const picked = useMemo(() => (pickedBin ? lookup(pickedBin) : null), [pickedBin, lookup]);
  return { pickedBin, setPickedBin, popRef, place, picked };
}

// The session column: the session's own summary as the strong line (the
// judge's newest summary standing in, marked, when the session has none), then
// the review facts.
function SessionCell({ session, sensitive }) {
  const loud = session.tone === "warn" || session.tone === "danger";
  const rungs = [session.worstBehaviour, session.worstContent].filter(Boolean).join(" \u00b7 ");
  const since = session.firstAt != null ? `since ${dayLabelFormatter.format(session.firstAt)} ${timeLabelFormatter.format(session.firstAt)}` : null;
  const facts = [
    `${session.reviewCount} review${session.reviewCount === 1 ? "" : "s"}`,
    rungs || null,
    `${session.findings} finding${session.findings === 1 ? "" : "s"}`,
    since,
    session.complete ? null : "older reviews not loaded",
  ].filter(Boolean).join(" \u00b7 ");
  const summary = sensitive ? session.summary : "";
  return (
    <div className="grid min-w-0 gap-[2px]">
      {summary ? (
        <strong className="truncate text-[11px] leading-[1.08] font-medium">
          {summary}
          {session.summaryFrom === "review" ? <span className="font-normal text-[color:var(--muted-soft)]"> (the judge&rsquo;s words)</span> : null}
        </strong>
      ) : (
        <span className="truncate text-[11px] leading-[1.08] text-[color:var(--muted-soft)]">
          {sensitive ? `trace ${session.id.slice(0, 8)}` : "Summary hidden. Sensitive read required."}
        </span>
      )}
      <span className={FACTS} style={{ color: loud ? SEVERITY_COLOR[session.tone] : "var(--muted-soft)" }}>{facts}</span>
    </div>
  );
}

// The machine a session ran on over the person registered to it, the first
// line in the trace log's own endpoint type.
function EndpointCell({ endpointId }) {
  const machine = machineOf(endpointId);
  return (
    <div data-reviews-endpoint="" className="grid min-w-0 gap-[2px]">
      <strong className="truncate text-[11px] leading-[1.08] font-medium text-[color:var(--muted)]">{machine?.host || "unattributed"}</strong>
      {machine?.user ? <span data-reviews-user="" className="truncate text-[9px] leading-[1.2] text-[color:var(--muted-soft)]">{machine.user}</span> : null}
    </div>
  );
}

// What a review did with a finding it did not keep, in a word on the finding's
// line and a sentence above its quotes.
const DROPPED_LABEL = { withheld: "withheld", context_only: "context only", unsupported: "unsupported" };
const DROPPED_REASON = {
  withheld: "It cleared none of the bars for keeping a finding: medium severity or worse, confidential content or worse, an upheld policy, intent or norm breach, or an injection.",
  context_only: "The quotes that were found sit in earlier turns the review was shown for context, not in the turns it reviewed; an earlier review covers those.",
  unsupported: "None of its quotes were found in the trace.",
};
const NO_HOVER = () => {};

// Beneath an opened session's kept findings, one line for the ones its
// reviews wrote and did not keep: how many their headers count, why, once the
// read lands, and how many of them it could not return. It opens to the
// findings themselves when there are any to show.
function DroppedHead({ dropped, open, inset, onToggle }) {
  const status = dropped?.status || "loading";
  const count = dropped?.count || 0;
  const kinds = new Map();
  for (const f of dropped?.findings || []) kinds.set(f.droppedAs, (kinds.get(f.droppedAs) || 0) + 1);
  const facts = ["withheld", "context_only", "unsupported"].filter((k) => kinds.get(k)).map((k) => `${kinds.get(k).toLocaleString()} ${DROPPED_LABEL[k]}`);
  if (dropped?.expired) facts.push(`${dropped.expired.toLocaleString()} whose details have aged out`);
  if (dropped?.truncated) facts.push(`only the first ${DROPPED_FINDINGS_LIMIT.toLocaleString()} were read`);
  const note = status === "loading" ? "Reading them" : status === "error" ? "They could not be read." : facts.join(" \u00b7 ");
  const openable = status !== "done" || (dropped?.findings || []).length > 0;
  return (
    <span data-reviews-dropped={status} aria-busy={status === "loading" || undefined} className="flex min-w-0 items-center gap-x-[8px]" style={{ paddingLeft: inset }}>
      {openable ? (
        <Disclosure data-reviews-dropped-toggle="" open={open} label={open ? "Hide the findings the reviews did not keep" : "Show the findings the reviews did not keep"} onToggle={onToggle} />
      ) : <span className="w-[14px] shrink-0" aria-hidden="true" />}
      <span className={`${FILE_LABEL} shrink-0 leading-[15px]`}>Not kept{count ? `${" \u00b7 "}${count.toLocaleString()} ${plural(count, "finding")}` : ""}</span>
      <span data-reviews-dropped-facts="" role={status === "error" ? "alert" : undefined} className="min-w-0 truncate text-[9px] leading-[15px] text-[color:var(--muted)]">{note}</span>
    </span>
  );
}

// An opened session's head, across the row under its summary: where its
// findings' evidence sits on its turns, or why that cannot be drawn, beside
// the way into the session.
function OpenedSessionHead({ session, entry, sensitive, caps, inset, picked, onPick, highlight, openFindings, indexOf, onOpen, onInvestigate = null }) {
  const open = (
    <button type="button" data-reviews-open-session="" onClick={(e) => { e.stopPropagation(); onOpen(); }} className={ACTION_CHIP}>
      Open the trace
    </button>
  );
  const loading = sensitive && (!entry || entry.status === "loading");
  if (loading) {
    return (
      <div data-reviews-opened="loading" aria-busy="true" className="grid cursor-auto gap-[6px] pt-[2px] pb-[6px]" style={{ paddingLeft: inset }}>
        <div className="flex flex-wrap items-baseline justify-between gap-x-[12px] gap-y-[2px]">
          <span className={FILE_LABEL}>Where each finding&rsquo;s evidence sits</span>
          {open}
        </div>
        <div className="grid min-w-0 gap-[1px]">
          <div className="relative h-[58px]">
            <span className="absolute inset-x-0 bottom-0 h-[1px]" style={{ backgroundImage: BASELINE }} aria-hidden="true" />
            <PlacingBars compact={false} caption="Reading this trace's findings" />
          </div>
          <span className="h-[12px]" aria-hidden="true" />
        </div>
      </div>
    );
  }
  let note = null;
  if (!sensitive) note = "Finding titles, quotes and where they sit require permission to read sensitive trace data.";
  else if (entry.status === "error") note = `This trace's findings could not be read. ${analyticsErrorCopy(entry.error, caps)?.message || ""}`.trim();
  else if (!entry.findings.length) note = "No findings were read for this trace's reviews in this date range.";
  if (note) {
    return (
      <div
        data-reviews-opened={entry?.status || "hidden"}
        role={entry?.status === "error" ? "alert" : undefined}
        className="flex cursor-auto items-baseline justify-between gap-x-[12px] pt-[4px] pb-[6px]"
        style={{ paddingLeft: inset }}
      >
        <span className="text-[11px] leading-[1.35] text-[color:var(--muted)]">{note}</span>
        {open}
      </div>
    );
  }
  return (
    <CaseTimeline
      c={{ id: `s:${session.id}`, count: entry.findings.length, sessionCount: 1 }}
      entry={entry.timeline}
      labels={null}
      machineFor={() => null}
      picked={picked}
      onPick={onPick}
      highlight={highlight}
      openFindings={openFindings}
      indexOf={indexOf}
      inset={inset}
      action={onInvestigate ? <><InvestigateChip onInvestigate={onInvestigate} />{open}</> : open}
      note={entry.truncated ? `Only the first ${REVIEW_FINDINGS_LIMIT.toLocaleString()} findings were read; the trace holds the rest.` : null}
    />
  );
}

// -- Triage: the backend's verification of each finding -----------------------
//
// Whether each finding stands against its own session, and then -- separately
// -- whether it meets the escalation standard in use. No rungs: this says
// "worth waking you" or not, which is a different axis from "how loud".
//
// Jobs the list shows: a job is one session's findings of one conduct, the
// unit the verifier reads. The worst 125 are listed; the funnel counts every
// finding in the window.
const LITE_LIMIT = 125;

// The scope's findings as the backend verified them. Verification runs in the
// trace-analysis pipeline, for every eligible finding of a tenant Origin enabled
// it for, so this reads rather than runs: a finding with no verdict yet is
// waiting for the pipeline, was reviewed before its tenant was enabled, or its
// tenant is not enabled.
function useLite({ conduct, scopeKey, window, sensitive = true, enabled = true }) {
  const map = useMemo(() => buildTriageMap(conduct), [conduct]);
  const pool = useMemo(() => [...(map?.findings?.values?.() || [])], [map]);
  // `for`: the findings the verdicts in hand were read for.
  const [state, setState] = useState({ status: "idle", verdicts: {}, error: null, for: null });
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const ids = useMemo(() => pool.map((f) => f.id), [pool]);
  const idsKey = ids.join(",");
  useEffect(() => {
    if (!enabled) return undefined;
    if (!ids.length) { setState({ status: "ready", verdicts: {}, error: null, for: idsKey }); return undefined; }
    const controller = new AbortController();
    setState((cur) => ({ ...cur, status: "loading", error: null }));
    // A reload is asked for because something changed; the cache would hand
    // back what was there before.
    fetchVerdicts({ ids, reviewWindow: window, sensitive, signal: controller.signal, fresh: nonce > 0 }).then(
      (verdicts) => { if (!controller.signal.aborted) setState({ status: "ready", verdicts, error: null, for: idsKey }); },
      (error) => { if (!controller.signal.aborted) setState({ status: "error", verdicts: {}, error, for: idsKey }); },
    );
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, window, sensitive, nonce, enabled]);
  const conductName = useCallback((f) => map?.families?.[f?.family]?.name || "", [map]);
  // Read (or refused) for the findings in the pool now: the render that brings
  // new findings still holds the verdicts of the ones before.
  const settled = (state.status === "ready" || state.status === "error") && state.for === idsKey;
  return { ...state, settled, map, conductName, pool, scopeKey, readWindow: window, sensitive, reload };
}

// The groups the Triage tab lists: only findings a standard flagged, inside
// the window, so the cap counts flagged groups and a group's other findings
// do not ride along. `verdicts` are as the standards in view answered them.
function liteSelection(map, verdicts, timeWindow) {
  const flagged = verdicts || {};
  const g = selectSessions(map, {
    limit: LITE_LIMIT,
    within: (f) => Boolean(flagged[f.id]?.escalates) && (!timeWindow || inLiteWindow(f, timeWindow)),
  });
  return { findings: g.jobs, total: g.total, covers: g.findings, sessions: g.sessions };
}

// How many findings one job settles: the count is the part that differs row
// to row, and what it counts is on hover.
function LiteCount({ job }) {
  const n = job.members?.length || 1;
  if (n < 2) return <CountChip count={n} />;
  return (
    <HoverTooltip label={`${n} findings on this trace, ${job.conducts > 1 ? `${job.conducts} conducts` : "one conduct"}`} placement="top">
      <span data-lite-group="" className="flex shrink-0"><CountChip count={n} /></span>
    </HoverTooltip>
  );
}

// What a row's findings came to, or that the verifier has not reached them.
// Which standards flagged the row's findings, by name: in a slot of its own
// width on every row, so the rows' columns line up, empty where none did.
function LiteFlaggedBy({ members, std }) {
  const names = flaggedBy(members, std).map((s) => s.name);
  if (!names.length) return <span data-lite-flagged-by="" className="w-[180px] shrink-0" aria-hidden="true" />;
  const text = `Flagged by ${names.join(", ")}`;
  return (
    <span data-lite-flagged-by="" className="flex w-[180px] min-w-0 shrink-0">
      <OverflowTooltip text={text} className="min-w-0 truncate text-[11px] leading-[15px] text-[color:var(--muted)]" />
    </span>
  );
}

// A row's second line, as a case's reason: why the standard met the job,
// else what the check made of the finding its title came from, else that
// nothing has read it yet.
function liteRowLine(job, verdicts) {
  const members = membersOf(job);
  const met = members.map((m) => verdicts?.[m.id]).find((v) => v?.escalates && v.standardWhy);
  if (met) return met.standardWhy;
  const lead = members.find((m) => m.title === job.title && verdicts?.[m.id]) || members.find((m) => verdicts?.[m.id]);
  if (lead) return verdicts[lead.id].why || "";
  return "not verified yet";
}

// A job's row, drawn as the Triage tab draws a case: the disclosure, the
// worst finding's severity and title over the line saying where the job
// stands, how many findings ride on it, the session and the machine it ran
// on, and what the job is doing or came to. Opened, the row sits on the
// lighter panel with its findings in the column under its title.
function LiteRow({ finding, verdicts, onOpen, conductName, chat = null, std = null, labels, machineFor, at = NaN, lit = false, reveal = null, onInspect = null }) {
  const [open, setOpen] = useState(false);
  // A click on the time window's pin opens the row and brings it into view.
  const rowRef = useRef(null);
  useEffect(() => {
    if (reveal == null) return;
    setOpen(true);
    rowRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }, [reveal]);
  const members = membersOf(finding);
  const mix = liteSessionMix(members, verdicts);
  const settled = mix.settled > 0;
  const toggle = () => setOpen((v) => !v);
  const sessionCase = useMemo(() => ({
    sessions: [finding.session],
    endpoints: [...new Set(membersOf(finding).map((m) => m.endpoint).filter(Boolean))],
    sessionLabel: null,
  }), [finding]);
  if (onInspect) return (
    <button
      type="button"
      data-triage-job={finding.id}
      onClick={() => onInspect(finding, labels?.get(finding.session) || null)}
      onMouseEnter={() => prefetchSessionPage(finding.session, members.map((m) => ({ messageIds: m.messages || [] })))}
      className="group grid w-full min-w-0 cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-x-[12px] border-t border-[color:var(--line)] bg-transparent px-[10px] py-[14px] text-left transition-colors hover:bg-[color:var(--control-bg)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--green)]"
    >
      <span className="grid min-w-0 gap-[6px]">
        <span className="flex min-w-0 items-baseline gap-[8px]">
          <SeverityWord severity={finding.severity} />
          <span className="min-w-0 text-[14px] font-medium leading-[1.4] text-[color:var(--ink)] [overflow-wrap:anywhere]">{finding.title || "Flagged trace"}</span>
        </span>
        <span className="text-[12px] leading-[1.4] text-[color:var(--muted)]">{liteRowLine(finding, verdicts)}</span>
        <span className="flex flex-wrap items-center gap-x-[12px] gap-y-[3px] text-[11px] leading-[1.4] text-[color:var(--muted-soft)]">
          <span>{labels?.get(finding.session) || "Trace " + String(finding.session).slice(0, 8)}</span>
          {Number.isFinite(at) ? <time dateTime={new Date(at).toISOString()}>{dayLabelFormatter.format(new Date(at))} {timeLabelFormatter.format(new Date(at))}</time> : null}
        </span>
      </span>
      <svg viewBox="0 0 16 16" className="h-[14px] w-[14px] text-[color:var(--muted-soft)] group-hover:text-[color:var(--ink)]" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true"><path d="m6 3 5 5-5 5"/></svg>
    </button>
  );
  return (
    <div
      ref={rowRef}
      data-lite-row=""
      data-lite-escalated={mix.escalated ? "" : undefined}
      data-lite-lit={lit ? "" : undefined}
      className={`grid min-w-0 border-t border-[color:var(--line-soft)] transition-colors ${open ? "bg-[color:var(--panel)]" : lit ? "bg-[color:var(--control-bg)] shadow-[inset_2px_0_0_var(--danger,#d4503e)]" : ""}`}
    >
      <div
        onClick={toggle}
        // Opening reads the session; start when the pointer arrives.
        onMouseEnter={() => prefetchSessionPage(finding.session, members.map((m) => ({ messageIds: m.messages || [] })))}
        className={`@container group/head relative flex min-w-0 cursor-pointer items-center gap-x-[12px] px-[6px] py-[8px] ${open ? "" : "hover:bg-[color:var(--control-bg)]"}`}
      >
        {/* How every kept standard answered this group, under the line the
            pointer rests on, so an open row's panel never pushes it out of
            view. The delay keeps a pointer passing down the list from opening
            a card on every row it crosses. Open, the row marks its met findings
            itself, so the card would only repeat it. Keyboard focus shows it;
            the focus a click leaves on the toggle does not, or the card would
            stay up after the pointer had gone. The card takes no pointer: it
            opens over the rows below, and a card that held the pointer kept
            itself up as the pointer moved down onto the next row. */}
        {std && settled && !open && boardInGroup(members, std).length ? (
          <div
            data-lite-std-hover=""
            className="pointer-events-none invisible absolute right-0 top-full z-20 opacity-0 transition-opacity duration-100 group-hover/head:visible group-hover/head:opacity-100 group-hover/head:delay-300 group-has-[:focus-visible]/head:visible group-has-[:focus-visible]/head:opacity-100"
            style={{ left: CASE_INSET }}
          >
            <div data-lite-std-hover-card="" className="pt-[2px]" style={{ width: `min(${STANDARDS_CARD_W}px, 100%)` }}>
              <div className={POPOVER_CARD}>
                <LiteStandardsCard members={members} std={std} />
              </div>
            </div>
          </div>
        ) : null}
        {Number.isFinite(at) ? (
          <time data-lite-time="" dateTime={new Date(at).toISOString()} title={fullTimestampFormatter.format(new Date(at))} className="w-[58px] shrink-0 text-[11px] leading-[15px] tabular-nums text-[color:var(--muted)]">
            {timeLabelFormatter.format(new Date(at))}
          </time>
        ) : null}
        <Disclosure data-lite-head="" open={open} label={open ? "Hide details" : "Show details"} onToggle={toggle} />
        <span className="grid min-w-0 flex-1 gap-[2px]">
          <span className="flex min-w-0 items-center gap-x-[8px]">
            <OverflowTooltip
              text={finding.title}
              className={`min-w-0 truncate text-[11px] leading-[1.15] text-[color:var(--ink)] ${open ? "font-semibold" : "font-medium"}`}
            />
          </span>
          {/* Opened, the row's own body says why; the head keeps its title. */}
          {open ? null : <span data-lite-row-line="" className="min-w-0 truncate text-[11px] leading-[1.15] text-[color:var(--muted)]">{liteRowLine(finding, verdicts)}</span>}
        </span>
        <span className="flex shrink-0 items-center gap-x-[10px]">
          <LiteCount job={finding} />
          {/* Opened, the card names the session and where it ran; the slot
              keeps its width so the row's other columns stay put. */}
          <span className={`hidden @min-[760px]:flex ${open ? "invisible" : ""}`} aria-hidden={open || undefined} data-lite-row-session="">
            <CaseSessions c={sessionCase} labels={labels} machineFor={machineFor} />
          </span>
          <LiteFlaggedBy members={members} std={std} />
          {chat?.available ? (
            // A slot as wide as the longer of its two words, the chip at its
            // end, so a row in the chat lines up with one that is not.
            <span data-lite-investigate-slot="" className="flex w-[128px] shrink-0 justify-end">
              <HoverTooltip label={chat.inChat ? "Go back to the chat about this trace" : "Opens a chat about this trace and asks what happened"} placement="top">
                <button
                  type="button"
                  data-lite-investigate=""
                  data-lite-in-chat={chat.inChat ? "" : undefined}
                  onClick={(e) => { e.stopPropagation(); chat.onInvestigate(finding); }}
                  className={`${ACTION_CHIP} inline-flex items-center gap-[5px] whitespace-nowrap ${chat.inChat ? "border-[color:var(--line-strong)] text-[color:var(--ink)]" : ""}`}
                >
                  {chat.inChat ? <span aria-hidden="true" className="h-[5px] w-[5px] rounded-full bg-[color:var(--ink)]" /> : null}
                  {chat.inChat ? "In chat" : "Investigate in chat"}
                </button>
              </HoverTooltip>
            </span>
          ) : null}
        </span>
      </div>
      {/* Opened, a row shows what each finding came to and where, its right
          edge where the head's is (its px-[6px]). */}
      {open ? <LiteSessionPanel job={finding} verdicts={verdicts} conductName={conductName} onOpen={onOpen} inset={CASE_INSET} insetEnd={6} summary={labels?.get(finding.session) || null} /> : null}
    </div>
  );
}

// A day as the review list heads it: "Today", "Yesterday", or "Saturday, Oct 3".
const DAY_HEADING = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "short", day: "numeric" });
function dayHeading(at, nowMs = Date.now()) {
  if (!Number.isFinite(at)) return "Undated";
  const day = new Date(at).toDateString();
  if (day === new Date(nowMs).toDateString()) return "Today";
  if (day === new Date(nowMs - 86400000).toDateString()) return "Yesterday";
  return DAY_HEADING.format(new Date(at));
}

// What the chats that suggest and tune standards work from, offered while
// the Reviews lens is open, whichever tab is showing: every loaded true or
// partly true finding, newest first, whatever window the list shows, since a
// suggestion is better for the whole history; and saving through the lens's
// standards, so the bar follows a check of older findings a chat starts.
function useTriageChatContext({ lite, std, span, timeWindow, enabled = true }) {
  const windowName = liteWindowName(span, timeWindow);
  const suggestWindow = timeWindow || span;
  const suggestIds = useMemo(
    () => (std ? (lite.pool || [])
      .filter((f) => ASKABLE_VERDICTS.includes(std.verdicts?.[f.id]?.verdict))
      .sort((a, b) => String(b.completedAt || "").localeCompare(String(a.completedAt || "")))
      .map((f) => f.id) : []),
    [std, lite.pool],
  );
  const poolName = liteWindowName(span, null);
  // Replaced in place as the lens changes, and withdrawn when it goes, so a
  // card in the chat never sees the lens blink out between two renders.
  useEffect(() => {
    if (std && enabled) setSuggestContext({
      ids: suggestIds, readWindow: lite.readWindow, poolName, runWindow: suggestWindow, windowName, verdicts: std.effective, standards: std.lib?.items || [],
      canWrite: std.canWrite, sensitive: std.sensitive, create: std.create, backfill: std.backfill, job: std.job,
      // For tuning a standard: what each version answered, of which verdict,
      // the findings' titles, and saving a standard's next version.
      answers: std.answers, rawVerdicts: std.verdicts, titleOf: (id) => lite.map?.findings?.get(id)?.title || "", revise: std.reviseItem,
      // Where and when a finding was, so a flag can be told from another of the same title.
      whereOf: (id) => {
        const f = lite.map?.findings?.get(id);
        return { machine: machineOf(f?.endpoint)?.text || "", at: f?.completedAt || "" };
      },
    });
  }, [std, suggestIds, lite.readWindow, lite.map, poolName, suggestWindow, windowName, enabled]);
  useEffect(() => (enabled ? () => setSuggestContext(null) : undefined), [enabled]);
}

function LiteList({ lite, std, scopeLabel, scopeCapped = false, onOpen, timeWindow = null, windowControl = null, span = null, sessionWindow = null, sessionLabel = null, pool = null, caps = null, onInspect = null }) {
  // "Investigate in chat": the whole grouped session opened as a chat that is
  // already asking. Only where a chat console is mounted to host it and can
  // send; which chat each job opened is remembered so the link goes back to
  // it rather than starting another.
  const canAsk = useSyncExternalStore(subscribeTriageChatHost, () => typeof getTriageChatHost()?.ask === "function", () => false);
  const [chats, setChats] = useState(() => readLiteChats(lite.scopeKey));
  useEffect(() => { setChats(readLiteChats(lite.scopeKey)); }, [lite.scopeKey]);
  const investigate = useCallback(async (job) => {
    const host = getTriageChatHost();
    const ref = chats[job.id];
    if (ref && host?.has?.(ref)) { host.open(ref); return; }
    if (typeof host?.ask !== "function") return;
    const { createReadTools } = await import("../../lib/triageReadKit.js");
    const opened = investigateLiteSession({
      job, verdicts: lite.verdicts, host, conductName: lite.conductName,
      // The session reading tools. A chat's reads are not rationed.
      createTools: () => createReadTools({ finding: job, context: { window: lite.readWindow } })
        .filter((t) => LITE_INVESTIGATE_TOOLS.includes(t.name)),
    });
    setChats((cur) => {
      const next = { ...cur, [job.id]: { chatId: opened.chatId, serverChatId: opened.serverChatId } };
      writeLiteChats(lite.scopeKey, next);
      return next;
    });
  }, [chats, lite.verdicts, lite.conductName, lite.readWindow, lite.scopeKey]);
  const chatFor = (job) => {
    if (onInspect || !canAsk) return null;
    const ref = chats[job.id];
    return { available: true, inChat: Boolean(ref && getTriageChatHost()?.has?.(ref)), onInvestigate: investigate };
  };
  // The sessions the rows name, called as the Triage tab calls them, with the
  // machine each ran on.
  const namedSessions = useMemo(() => [...new Set(lite.findings.map((f) => String(f.session).toLowerCase()))].sort(), [lite.findings]);
  const jobEndpoints = useMemo(() => [...new Set(lite.findings.flatMap((f) => membersOf(f).map((m) => m.endpoint)).filter(Boolean))], [lite.findings]);
  const { labels, machineFor } = useSessionIdentity({ map: lite.map, sessions: namedSessions, endpoints: jobEndpoints, window: sessionWindow, sessionLabel });
  const openSession = useCallback((f) => onOpen?.(f, labels?.get(f?.session) || null), [onOpen, labels]);
  // Page rows put the highest severity first, then the newest review.
  // Embedded queues retain their chronological order.
  const rows = useMemo(
    () => liteRows(lite.findings, lite.verdicts).sort((a, b) => (onInspect ? severityRank(b.finding.severity) - severityRank(a.finding.severity) : 0) || String(b.finding.completedAt).localeCompare(String(a.finding.completedAt))),
    [lite.findings, lite.verdicts, onInspect],
  );
  const total = rows.length;
  // The flagged groups by the day of their newest finding, in the reader's time.
  const days = useMemo(() => {
    const out = [];
    for (const r of rows) {
      const at = parseServerTimestamp(r.finding.completedAt);
      const key = Number.isFinite(at) ? new Date(at).toDateString() : "";
      if (!out.length || out[out.length - 1].key !== key) out.push({ key, at, rows: [] });
      out[out.length - 1].rows.push({ ...r, at });
    }
    return out;
  }, [rows]);
  // A met pin on the time window: the standards that met its findings, the
  // rows it lights while pointed at, and the rows a click on it opens.
  const metByOf = useCallback((r) => flaggedBy([{ id: String(r.finding_id) }], std).map((s) => s.name), [std]);
  const [lit, setLit] = useState(null);
  const [reveal, setReveal] = useState(null);
  const pickMet = useCallback((ids) => setReveal((cur) => ({ ids: new Set(ids), n: (cur?.n || 0) + 1 })), []);
  const holds = (f, ids) => !!ids && membersOf(f).some((m) => ids.has(String(m.id)));
  const row = (r) => (
    <LiteRow
      lit={holds(r.finding, lit)}
      reveal={holds(r.finding, reveal?.ids) ? reveal.n : null}
      key={r.finding.id}
      finding={r.finding}
      verdicts={lite.verdicts}
      onOpen={openSession}
      onInspect={onInspect}
      conductName={lite.conductName}
      chat={chatFor(r.finding)}
      std={std}
      labels={labels}
      machineFor={machineFor}
      at={r.at ?? parseServerTimestamp(r.finding.completedAt)}
    />
  );
  // Every finding in the window, not only the ones listed: a standard can be
  // put to any of them that has a verdict. Newest first, so a try asks the
  // newest first.
  const windowIds = useMemo(
    () => (lite.pool || [])
      .filter((f) => !timeWindow || inLiteWindow(f, timeWindow))
      .sort((a, b) => String(b.completedAt || "").localeCompare(String(a.completedAt || "")))
      .map((f) => f.id),
    [lite.pool, timeWindow],
  );
  const windowName = liteWindowName(span, timeWindow);
  // The Reviews lens offers the Triage data to the standards chats (see
  // useTriageChatContext); the buttons show when it holds findings to work from.
  const suggestCtx = useSyncExternalStore(subscribeSuggestContext, getSuggestContext, () => null);
  const canSuggest = Boolean(canAsk && std?.sensitive && lite.readWindow && suggestCtx?.ids?.length);
  const suggest = useCallback(() => {
    try { openStandardSuggestions({ host: getTriageChatHost() }); } catch { /* the button only shows where a chat can open */ }
  }, []);
  const tune = useCallback((item) => {
    try { openStandardTuning({ host: getTriageChatHost(), standardId: item.standardId }); } catch { /* the button only shows where a chat can open */ }
  }, []);
  const status = (pool?.status === "loading" || lite.status === "loading") && !total
    ? "Loading results..."
    : null;
  const blocked = poolBlocker(pool, caps, scopeLabel);
  if (blocked) {
    return (
      <div data-lite-list="" className="grid gap-[8px] pt-[12px]">
        <PoolBlocked text={blocked} error={pool?.status === "error"} />
      </div>
    );
  }
  return (
    <div data-lite-list="" aria-busy={lite.status === "loading" || undefined} className="grid gap-[8px] pt-[12px]">
      {!onInspect && windowControl ? <TriageTimeWindow {...windowControl} metByOf={metByOf} onHoverMet={setLit} onPickMet={pickMet} /> : null}
      <PoolLine pool={pool} />
      {!onInspect && std ? <LiteStandardBar std={std} windowIds={windowIds} windowName={windowName} runWindow={timeWindow || span} scopeLabel={std.scope ? scopeLabel : "the organization"} scopeCapped={scopeCapped} onSuggest={canSuggest ? suggest : null} onTune={canSuggest ? tune : null} /> : null}
      {lite.error ? (
        <div role="alert" data-lite-error="" className="grid gap-[4px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px]">
          <strong className="text-[13px] leading-[1.08] font-medium">Couldn&rsquo;t load the results: {String(lite.error?.serverMessage || lite.error?.message || "the request failed.").slice(0, 300)}</strong>
        </div>
      ) : null}
      {status ? (
        <span role="status" data-lite-empty="" className={NOTE_CLASS}>{status}</span>
      ) : lite.error ? null : (
        <div data-lite-inbox="" className="grid min-w-0">
          {lite.total > LITE_LIMIT ? <span data-lite-capped="" className={`${NOTE_CLASS} px-[6px]`}>Showing {LITE_LIMIT} of {lite.total.toLocaleString()}. Pick a shorter time range to see the rest.</span> : null}
          {total ? (
            <div data-lite-section="met" className="grid min-w-0">
              {onInspect ? rows.map(row) : days.map((d) => (
                <div key={d.key || "undated"} data-lite-day={d.key} className="grid min-w-0">
                  <span className="px-[6px] pt-[12px] pb-[4px] text-[12px] leading-[1.2] text-[color:var(--muted)]">{dayHeading(d.at)}</span>
                  {d.rows.map(row)}
                </div>
              ))}
            </div>
          ) : (
            <span data-lite-inbox-empty="" className="px-[6px] pt-[10px] text-[12px] leading-[1.35] text-[color:var(--ink)]">Nothing to review{timeWindow ? ` in ${windowName}` : ""}.</span>
          )}
        </div>
      )}
      {onInspect && pool?.hasMore && !status && !lite.error ? <button type="button" onClick={pool.loadMore} disabled={pool.loadingMore} aria-busy={pool.loadingMore || undefined} className={`w-fit ${buttonClass("secondary")}`}>{pool.loadingMore ? "Loading older..." : "Load older"}</button> : null}
    </div>
  );
}

// What the Triage tab reads for a scope and range: the scope's sessions, the
// findings pool, Triage's verdicts on it and the standards in use, with the
// window its list is narrowed to. Shared by the lens, whose flow above the
// tabs counts the same findings, by the welcome page's Triage section, whose
// queue (TriageQueue) and cases (CaseInbox) read it once, and by a
// workspace's cases. Only a page's own reader (`publish`) offers its findings
// to the chats that suggest and tune standards: one in a workspace's dock
// reads a few sessions, and would take the page's findings away from them as
// it came and went.
export function useTriageReads({ scopePath, range, steer = "", caps, publish = true, followRuns = true, findingsLimit, escalated = null }) {
  const sensitive = hasPermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);
  const scopeKey = encodeScopePath(scopePath) || "org";
  const ids = useScopeSessionIds({ scopePath, range, steer, sensitive });
  // The scope's findings pool, which the Triage and Investigate tabs read and
  // which gives each session in the Findings tab its dominant conduct.
  const conductState = useScopeConduct({ ids, sensitive, limit: findingsLimit, escalated });
  // Verdicts are read on every visit, not once Triage is opened: the flow
  // above the tabs counts them.
  const lite = useLite({ conduct: conductState.conduct, scopeKey, window: ids.window, sensitive });
  // The Triage tab reads only what Triage checked, so its window and the
  // timeline it is dragged on cover those findings: a spike of findings
  // reviewed before Triage ran would offer a window that lists nothing.
  const checkedRows = useMemo(
    () => conductState.rows.filter((r) => lite.verdicts?.[String(r?.finding_id)]),
    [conductState.rows, lite.verdicts],
  );
  const liteCovers = useMemo(() => poolSpan(checkedRows), [checkedRows]);
  // Which findings become Triage's jobs, by when they were reviewed, in the
  // shared window control. Per visit, and anew for each scope and range: the
  // window the reader wanted yesterday is not the one they want now, and a
  // window over another pool can only hold nothing.
  // Opens on everything loaded; a narrower window is the reader's pick.
  const [liteWindow, setLiteWindow] = useState(null);
  useEffect(() => { setLiteWindow(null); }, [scopeKey, ids.window.fromMs, ids.window.toMs]);
  const canWriteStandards = hasPermission(caps, PERMISSIONS.TENANT_CONFIG_WRITE);
  // A re-apply covers what the lens is scoped to: the whole organization at the
  // root, else the sessions the scope resolved to, as many as a run may name.
  const runScope = useMemo(() => (ids.direct ? null : ids.sessionIds.slice(0, MAX_RUN_SCOPE)), [ids.direct, ids.idsKey]);
  const runScopeCapped = !ids.direct && (ids.sessionIds.length > MAX_RUN_SCOPE || ids.sessionsCapped);
  const liteStd = useLiteStandards({ scopeKey, lite, canWrite: canWriteStandards, scope: runScope, followRuns });
  // The list reads the verdicts as the standard in use answered them.
  const liteListed = useMemo(() => liteSelection(lite.map, liteStd.effective, liteWindow), [lite.map, liteStd.effective, liteWindow]);
  const liteShown = useMemo(() => ({ ...lite, ...liteListed, verdicts: liteStd.effective }), [lite, liteListed, liteStd.effective]);
  useTriageChatContext({ lite: liteShown, std: liteStd, span: liteCovers, timeWindow: liteWindow, enabled: publish });
  // The findings a standard in view escalated, which the Triage tab's time
  // window marks.
  const kept = useMemo(() => liteKept(liteStd.effective), [liteStd.effective]);
  // Triage's columns wait for its verdicts and the standards' answers: drawn
  // before them, every kept finding would read as not checked for a moment.
  // A failed read of either says so in their place.
  // Only findings read for the sessions in reach now count: in the render a
  // search or scope changes in, they are still the ones read for the last.
  const current = conductState.idsKey === ids.idsKey;
  const failed = current && conductState.status === "done" && lite.settled && (lite.status === "error" || (liteStd.answersReady && Boolean(liteStd.answersError)));
  const read = current && conductState.status === "done" && lite.settled && liteStd.answersReady && !failed;
  return { ids, conductState, lite, checkedRows, liteCovers, liteWindow, setLiteWindow, runScopeCapped, liteStd, liteShown, kept, failed, read };
}

// The Triage tab's list on a page of its own: what a standard flagged in the
// scope and range, by day, the newest first, each group opening to its
// session and findings and to a chat that investigates it, from what
// useTriageReads read (`t`, with the reader's `caps`), so a page that also
// shows those findings another way reads them once. `onOpen(finding, label)`
// opens a finding's session; `onCount(n)` hears how many groups are listed
// once Triage's reads are in (null until then).
export function TriageQueue({ t, caps, scopeLabel = "the organization", onOpen, onCount = null, onInspect = null }) {
  const count = t.read ? liteRows(t.liteShown.findings, t.liteShown.verdicts).length : null;
  useEffect(() => { onCount?.(count); }, [count, onCount]);
  return (
    <LiteList
      lite={onInspect && !t.read ? { ...t.liteShown, findings: [], status: t.failed ? "error" : "loading", error: t.liteShown.error || t.liteStd.answersError || (t.failed ? new Error("The results could not be loaded. Reload to try again.") : null) } : t.liteShown}
      onInspect={onInspect}
      std={t.liteStd}
      timeWindow={t.liteWindow}
      windowControl={{
        rows: t.checkedRows, window: t.liteWindow, onChange: t.setLiteWindow,
        marked: t.kept.escalated,
        canLoadMore: t.conductState.hasMore, loadingMore: t.conductState.loadingMore, onLoadMore: t.conductState.loadMore,
      }}
      span={t.liteCovers}
      scopeLabel={scopeLabel}
      scopeCapped={t.runScopeCapped}
      sessionWindow={t.ids.window}
      pool={t.conductState}
      caps={caps}
      onOpen={onOpen}
    />
  );
}

export default function ReviewsBody({ nav, ctx }) {
  const scopePath = nav?.scopePath || [];
  const range = nav?.range || ctx?.range || null;
  const steer = ctx?.query || "";
  const caps = useMyPermissions();
  // Fail-open while caps load, as the overview's own prompt reads do: the
  // prose columns drop out only once the role RESOLVES without the grant.
  const sensitive = hasPermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);
  const terminal = scopePath.length ? scopePath[scopePath.length - 1] : null;
  const scopeLabel = terminal ? scopeStepLabel(terminal) : "Organization";
  const scopeKey = encodeScopePath(scopePath) || "org";
  // The endpoint column, as the trace log decides it: hidden inside one endpoint.
  const showEndpoint = ctx?.traceLog?.showEndpoint ?? terminal?.dim !== "endpoints";
  const selectedTraceId = String(ctx?.traceLog?.selectedTraceId || "");

  const { ids, conductState, lite, checkedRows, liteCovers, liteWindow, setLiteWindow, runScopeCapped, liteStd, liteShown, kept, failed: triageFailed, read: triageRead } = useTriageReads({ scopePath, range, steer, caps });
  const sessions = useScopeReviewSessions({ ids, sensitive, conduct: conductState.conduct });
  const result = sessions.result;
  // Which list shows under the head: the findings by session, Triage's check
  // of each one, or the Investigate pass across sessions.
  const [tab, setTab] = useState("sessions");
  // A session the Findings tab sent to Investigate: its case opens there.
  const [investigateFocus, setInvestigateFocus] = useState(null);
  const sessionLabelOf = useCallback((id) => {
    const row = result?.sessions?.find((s) => s.id === id);
    return row?.summary || null;
  }, [result]);
  // The flow above the tabs: the report headers' sums, then the pool as the
  // Triage tab reads it, against its verdicts under the standard in view.
  const flowSums = useReviewFlowSums({ ids });
  // It waits for the sums, so it never redraws from the kept findings back to
  // what was written; the pool fills Triage's columns when it lands (once
  // Triage's reads are in, `triageRead`; a failed one says so in their place).
  // The last flow drawn with Triage's columns, kept while the same sums are
  // read again, so a re-read of the verdicts never empties them.
  const lastFlow = useRef(null);
  const flowModel = useMemo(() => {
    if (flowSums.status !== "done" && flowSums.status !== "error") return null;
    if (!triageRead && lastFlow.current?.sums === flowSums.sums && conductState.status === "done") return lastFlow.current.model;
    const since = flowSums.since;
    const findings = triageRead
      ? (since == null ? lite.pool : lite.pool.filter((f) => parseServerTimestamp(f.completedAt) >= since))
      : null;
    if (!flowSums.sums && !findings) return null;
    // A pool that reaches back past Triage's start holds every finding since.
    const reachesSince = since != null && lite.pool.some((f) => parseServerTimestamp(f.completedAt) < since);
    const model = buildReviewFlow({ judged: flowSums.sums, findings, clipped: conductState.clipped && !reachesSince, verdicts: liteStd.effective });
    lastFlow.current = findings ? { sums: flowSums.sums, model } : null;
    return model;
  }, [flowSums.status, flowSums.sums, flowSums.since, conductState.status, conductState.clipped, lite.pool, triageRead, liteStd.effective]);
  const flowPoolNote = poolBlocker(conductState, caps, scopeLabel) || (conductState.status !== "done" ? POOL_READING : triageRead ? "" : triageFailed ? "Couldn\u2019t read what Triage checked." : "Reading what Triage checked\u2026");
  const flowStandard = liteStd.any ? `any of ${liteStd.lib.items.length} standards` : standardLabel(liteStd.inUse);

  // Which sessions are open, and which of their findings show what the judge
  // quoted; a new scope or range closes them all.
  const [expanded, setExpanded] = useState(() => new Set());
  const [openFindings, setOpenFindings] = useState(() => new Set());
  const [openDropped, setOpenDropped] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("latest");
  const rangeKey = `${range?.from ?? ""}-${range?.to ?? ""}`;
  useEffect(() => { setExpanded(new Set()); setOpenFindings(new Set()); setOpenDropped(new Set()); setSearch(""); }, [scopeKey, rangeKey]);
  const findingsOf = useSessionFindings({ result, expanded, window: ids.window, sensitive });

  const columns = useMemo(() => sessionColumns(showEndpoint), [showEndpoint]);
  const cols = useDataTableColumns({ columns, defaultVisible: columns.map((c) => c.id) });

  // Endpoint names from the lazy label cache, as TraceTable does: subscribe
  // for the rows' endpoints and re-render the cells as names land.
  const endpointSteps = useMemo(() => {
    if (!result) return [];
    const ids = [...new Set(result.sessions.map((s) => s.endpointId).filter(Boolean))];
    return ids.map((id) => ({ dim: "endpoints", id }));
  }, [result]);
  const labelTick = useLabels(endpointSteps);

  const visibleSessions = useMemo(() => {
    if (!result) return [];
    return sortSessions(filterSessions(result.sessions, search, { endpointName: (id) => machineOf(id)?.text ?? "unattributed" }), sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, search, sort, labelTick]);

  // The table's rows: each session, and beneath an open one its head, then
  // its findings, numbered, each followed by what the judge quoted when open,
  // then the line for the ones its reviews did not keep and, open, those.
  const rows = useMemo(() => {
    const out = [];
    for (const s of visibleSessions) {
      out.push({ kind: "session", rowId: `s:${s.id}`, session: s });
      if (!expanded.has(s.id)) continue;
      out.push({ kind: "head", rowId: `s:${s.id}/head`, session: s });
      const entry = findingsOf.get(s.id);
      (entry?.findings || []).forEach((f, i) => {
        const rowId = `s:${s.id}/${f.id}`;
        out.push({ kind: "finding", rowId, session: s, finding: f, index: i + 1 });
        if (openFindings.has(rowId)) out.push({ kind: "cite", rowId: `${rowId}/cite`, session: s, finding: f, index: i + 1 });
      });
      if (entry?.status !== "done" || !entry.dropped || entry.dropped.status === "none") continue;
      out.push({ kind: "dropped", rowId: `s:${s.id}/dropped`, session: s });
      if (!openDropped.has(s.id)) continue;
      for (const f of entry.dropped?.findings || []) {
        const rowId = `s:${s.id}/${f.id}`;
        out.push({ kind: "dropped-finding", rowId, session: s, finding: f });
        if (openFindings.has(rowId)) out.push({ kind: "dropped-cite", rowId: `${rowId}/cite`, session: s, finding: f });
      }
    }
    return out;
  }, [visibleSessions, expanded, openFindings, openDropped, findingsOf]);

  const toggle = useCallback((id) => setExpanded((cur) => flipped(cur, id)), []);
  const toggleFinding = useCallback((rowId) => setOpenFindings((cur) => flipped(cur, rowId)), []);
  const toggleDropped = useCallback((id) => setOpenDropped((cur) => flipped(cur, id)), []);

  // Opening a session: the same door the Sessions log opens, at a turn when one
  // is named, and seeded with the session row when the scoped read has it, so
  // the panel lands on the conversation rather than a placeholder. A turn is
  // numbered as the Lanes axis prints it; it opens at its message when one
  // came with it or placement has read the page that holds it, and at that
  // index otherwise, which is the same turn while the record fits one page.
  const openSessionAt = useCallback(({ session, turn = null, message = null, seed = null }) => {
    const focus = parseTurnFocusMessage(message) || (Number.isInteger(turn) ? recordMessageAt(session, turn) : null);
    if (focus) writeUrlParam(TURN_FOCUS_PARAM, focus);
    else if (Number.isInteger(turn)) writeUrlParam(TURN_FOCUS_PARAM, formatTurnFocus(Math.max(0, turn)));
    ctx?.traceLog?.onSelectTrace?.(session, seed);
  }, [ctx]);

  // An opened session's findings sit under its summary: the panel's content
  // starts where the session column does, past the row's 8px gaps.
  const panelInset = useMemo(() => {
    let x = 0;
    for (const c of cols.effective) {
      if (c.id === "session") break;
      x += c.width + 8;
    }
    return x;
  }, [cols.effective]);

  // One picked bar across the list and its card, and the finding the pointer
  // is on, whose bars darken on its session's timeline.
  const wrapRef = useRef(null);
  const lookupBin = useCallback((pb) => {
    const entry = findingsOf.get(pb.caseId.slice(2));
    const s = entry?.timeline?.sessions[0];
    if (!s || !(s.total > 0)) return null;
    const bin = binFindings(s.findings, s.turns, s.total, TIMELINE_BINS)[pb.bin];
    return bin?.findings.length ? { bin, turns: s.turns, order: new Map(entry.findings.map((f, i) => [f.id, i + 1])) } : null;
  }, [findingsOf]);
  const { pickedBin, setPickedBin, popRef, place, picked } = useBinCard(wrapRef, lookupBin);
  const [hovered, setHovered] = useState(null);
  // A finding picked from the card opens in the list, where its quotes are.
  const chooseFinding = useCallback((caseId, findingId) => {
    setOpenFindings((cur) => openRow(cur, `${caseId}/${findingId}`));
    setPickedBin(null);
    scrollToFinding(wrapRef, findingId);
  }, [setPickedBin]);

  const renderCell = useMemo(() => (row, col) => {
    const { session } = row;
    switch (col.id) {
      case "open": {
        const open = expanded.has(session.id);
        return <Disclosure data-session-toggle="" open={open} label={open ? "Close the trace's findings" : "Show the trace's findings"} onToggle={() => toggle(session.id)} />;
      }
      case "time":
        return session.latestAt != null
          ? renderTraceCell({ timestamp: session.latestAt }, col, {})
          : <span className="text-[10px] leading-[1.12] text-[color:var(--muted-soft)]">unknown</span>;
      case "session":
        return <SessionCell session={session} sensitive={sensitive} />;
      case "endpoint":
        return <EndpointCell endpointId={session.endpointId} />;
      case "conduct":
        return <ConductCell session={session} sensitive={sensitive} />;
      case "timeline":
        return <SessionTimelineStrip session={session} sensitive={sensitive} />;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensitive, expanded, toggle, labelTick]);

  // Beneath an open session, across the row: its head, its findings and what
  // an open one quoted.
  const renderFullRow = useCallback((row) => {
    if (row.kind === "session") return null;
    const { session } = row;
    const entry = findingsOf.get(session.id) || null;
    const caseId = `s:${session.id}`;
    const turns = entry?.timeline?.sessions[0]?.turns || null;
    const seed = session.session || null;
    if (row.kind === "head") {
      const order = new Map((entry?.findings || []).map((f, i) => [f.id, i + 1]));
      return (
        <OpenedSessionHead
          session={session}
          entry={entry}
          sensitive={sensitive}
          caps={caps}
          inset={panelInset}
          picked={pickedBin?.caseId === caseId ? pickedBin : null}
          onPick={setPickedBin}
          highlight={hovered}
          openFindings={new Set((entry?.findings || []).filter((f) => openFindings.has(`${caseId}/${f.id}`)).map((f) => f.id))}
          indexOf={(id) => order.get(id)}
          onOpen={() => openSessionAt({ session: session.id, seed })}
          onInvestigate={entry?.status === "done" && entry.findings.length ? () => {
            setInvestigateFocus({ session: session.id, findings: entry.findings.map((f) => String(f.id).toLowerCase()), n: Date.now() });
            setTab("triage");
          } : null}
        />
      );
    }
    if (row.kind === "cite") return <FindingQuotes f={row.finding} turns={turns} inset={panelInset} onOpen={(at) => openSessionAt({ ...at, seed })} />;
    if (row.kind === "dropped") {
      return <DroppedHead dropped={entry?.dropped || null} open={openDropped.has(session.id)} inset={panelInset} onToggle={() => toggleDropped(session.id)} />;
    }
    const droppedTurns = entry?.dropped?.turns || null;
    if (row.kind === "dropped-cite") {
      return <FindingQuotes f={row.finding} turns={droppedTurns} reason={DROPPED_REASON[row.finding.droppedAs]} inset={panelInset} onOpen={(at) => openSessionAt({ ...at, seed })} />;
    }
    if (row.kind === "dropped-finding") {
      return (
        <FindingLine
          f={row.finding}
          c={{ id: caseId, count: 0, sessionCount: 1, why: "" }}
          index={null}
          note={DROPPED_LABEL[row.finding.droppedAs]}
          quiet
          open={openFindings.has(row.rowId)}
          span={findingSpan(row.finding, droppedTurns)}
          placing={Boolean(entry?.dropped?.placing)}
          inset={panelInset}
          onToggle={toggleFinding}
          onHover={NO_HOVER}
        />
      );
    }
    return (
      <FindingLine
        f={row.finding}
        c={{ id: caseId, count: entry?.findings.length || 0, sessionCount: 1, why: "" }}
        index={row.index}
        open={openFindings.has(row.rowId)}
        span={findingSpan(row.finding, turns)}
        placing={entry?.timeline?.status === "loading"}
        inset={panelInset}
        onToggle={toggleFinding}
        onHover={setHovered}
      />
    );
  }, [findingsOf, sensitive, caps, panelInset, pickedBin, setPickedBin, hovered, openFindings, openDropped, openSessionAt, toggleFinding, toggleDropped]);

  const isActive = (row) => Boolean(row.session.id) && (selectedTraceId === row.session.id || selectedTraceId.startsWith(`${row.session.id}@`));
  // An open session and its findings sit on the lighter panel; what is read,
  // not clicked, keeps it under the pointer. The session the detail panel
  // shows keeps the log's own active mark.
  const rowClass = (row) => {
    if (row.kind === "session") {
      if (isActive(row)) return traceRowClassName({ active: true, isCtx: false });
      return expanded.has(row.session.id) ? PANEL_ROW : "";
    }
    return row.kind === "finding" || row.kind === "dropped" || row.kind === "dropped-finding" ? PANEL_ROW : PANEL_STILL_ROW;
  };

  const loading = sessions.status === "loading";
  let note = null;
  if (sessions.status === "unsupported") {
    note = "This scope has no server-side trace filter, so its reviews cannot be listed. Step up to the parent scope.";
  } else if (sessions.status === "denied") {
    note = "Listing the traces in this scope requires permission to read AI traces.";
  } else if (sessions.status === "error") {
    note = analyticsErrorCopy(sessions.error, caps).message;
  } else if (sessions.status === "done" && !sessions.direct && sessions.sessionCount === 0) {
    note = `No traces in scope for this date range, so there are no reviews to list for ${scopeLabel}.`;
  } else if (sessions.status === "done" && result && !result.sessions.length) {
    note = `No traces with findings in this date range for ${scopeLabel}.`;
  } else if (sessions.status === "done" && result && !visibleSessions.length) {
    note = "No traces match this search. Clear the search to bring them back.";
  }
  // A conduct carried by more sessions than one read can name lists the
  // first cap's worth and says so.
  const summary = sessionsSummaryParts(result, { shown: visibleSessions.length, totals: sessions.totals, sessionsCapped: ids.sessionsCapped });
  const remaining = sessions.remaining;
  const minWidth = showEndpoint ? 750 : 670;
  const footer = sessions.hasMore || sessions.moreError || (sessions.atCeiling && remaining > 0) ? (
    <div className="grid place-items-center gap-[6px] pt-[8px] pb-[4px]">
      {sessions.moreError ? (
        <span className={NOTE_CLASS} role="alert">{analyticsErrorCopy(sessions.moreError, caps).message}</span>
      ) : null}
      {sessions.hasMore ? (
        <button
          type="button"
          data-reviews-load-more=""
          onClick={sessions.loadMore}
          disabled={sessions.loadingMore}
          className={buttonClass("secondary", "sm")}
        >
          {sessions.loadingMore ? "Loading" : sessions.moreError ? "Try again" : `Load more \u00b7 ${formatCompact(remaining)} remaining`}
        </button>
      ) : sessions.atCeiling && remaining > 0 ? (
        <span className={NOTE_CLASS} data-reviews-ceiling="">
          The list stops at {SESSION_OFFSET_CEILING.toLocaleString()} traces. Narrow the scope or the date range to reach the other {formatCompact(remaining)}.
        </span>
      ) : null}
    </div>
  ) : null;
  const showActivity = sessions.status !== "unsupported" && sessions.status !== "denied";

  // One scroller: the chart above, then the head with three tabs the way the
  // overview's log head has them, and beneath it the list the tab names. The
  // tabs read as the escalation: Findings, what the judge raised, by session;
  // Triage, each finding verified against its own session and the standard;
  // Investigate, every finding (or those Triage kept) analysed across
  // sessions.
  const liteTab = showActivity && tab === "lite";
  const triageTab = showActivity && tab === "triage";
  const sessionsTab = !liteTab && !triageTab;
  const tabClass = (active, align) => [
    "relative grid gap-[2px] rounded-t-[7px] border border-b-0 px-[11px] py-[8px]", align,
    active ? "border-[color:var(--line-strong)] bg-[color:var(--bg)] cursor-default" : "border-[color:var(--line)] bg-[color:var(--control-bg)] cursor-pointer",
  ].join(" ");
  const eyebrowClass = "text-[12px] leading-[1.1] text-[color:var(--muted)]";
  // The active tab's title carries the weight; the others sit lighter so three
  // tabs do not read as three headings.
  const titleClass = (active) => ["text-[14px] leading-[1.06] tracking-[-0.04em]", active ? "font-medium text-[color:var(--ink)]" : "font-normal text-[color:var(--muted)]"].join(" ");
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <ScrollArea data-content-scroll className="min-h-0 flex-1" contentClassName="grid gap-[12px] pt-[10px] pb-[28px]">
        {showActivity ? (
          <ReviewFlow
            model={flowModel}
            status={flowSums.status === "idle" ? "loading" : flowSums.status}
            error={flowSums.error}
            errorText={flowSums.error ? analyticsErrorCopy(flowSums.error, caps).message : ""}
            poolNote={flowPoolNote}
            standardName={flowStandard}
            since={flowSums.since}
            emptyText={`No findings in this date range for ${scopeLabel}.`}
            onOpen={(next, outcome) => {
              setTab(next);
              // What a standard flagged is listed across everything loaded, as the flow counts it.
              if (outcome === "met") setLiteWindow(null);
            }}
          />
        ) : null}
        <div data-reviews-lens="" data-scope-key={scopeKey} data-lock-width>
          <div className="relative grid items-stretch gap-[2px]" style={{ gridTemplateColumns: showActivity ? "1fr 1fr 1fr" : "1fr" }}>
            <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[1px] bg-[color:var(--line)]" aria-hidden="true" />
            <button
              type="button"
              data-reviews-tab="sessions"
              data-active={sessionsTab ? "" : undefined}
              onClick={sessionsTab ? undefined : () => setTab("sessions")}
              className={tabClass(sessionsTab, "text-left")}
            >
              {sessionsTab && <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[1px] bg-[color:var(--bg)]" aria-hidden="true" />}
              <span className={eyebrowClass}>Trace review</span>
              <span className={titleClass(sessionsTab)}>Findings</span>
            </button>
            {showActivity ? (
              <button
                type="button"
                data-reviews-tab="lite"
                data-active={liteTab ? "" : undefined}
                onClick={liteTab ? undefined : () => setTab("lite")}
                className={tabClass(liteTab, "text-left")}
              >
                {liteTab && <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[1px] bg-[color:var(--bg)]" aria-hidden="true" />}
                <span className={eyebrowClass}>Verification</span>
                <span className={titleClass(liteTab)}>Triage</span>
              </button>
            ) : null}
            {showActivity ? (
              <button
                type="button"
                data-reviews-tab="triage"
                data-active={triageTab ? "" : undefined}
                onClick={triageTab ? undefined : () => setTab("triage")}
                className={tabClass(triageTab, "text-right")}
              >
                {triageTab && <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[1px] bg-[color:var(--bg)]" aria-hidden="true" />}
                <span className={eyebrowClass}>Analysis</span>
                <span className={titleClass(triageTab)}>Investigate</span>
              </button>
            ) : null}
          </div>

          {liteTab ? (
            <LiteList
              lite={liteShown}
              std={liteStd}
              timeWindow={liteWindow}
              windowControl={{
                rows: checkedRows, window: liteWindow, onChange: setLiteWindow,
                marked: kept.escalated,
                canLoadMore: conductState.hasMore, loadingMore: conductState.loadingMore, onLoadMore: conductState.loadMore,
              }}
              span={liteCovers}
              scopeLabel={scopeLabel}
              scopeCapped={runScopeCapped}
              sessionWindow={ids.window}
              sessionLabel={sessionLabelOf}
              pool={conductState}
              caps={caps}
              onOpen={(finding) => {
                if (finding.messages?.length) writeUrlParam(TURN_FOCUS_PARAM, formatTurnFocus(0, finding.messages[0]));
                ctx?.traceLog?.onSelectTrace?.(finding.session, null);
              }}
            />
          ) : triageTab ? (
            <InvestigateTab
              scopeKey={scopeKey}
              scopeLabel={scopeLabel}
              lite={lite}
              std={liteStd}
              pool={conductState}
              window={ids.window}
              sessionLabel={sessionLabelOf}
              blocked={poolBlocker(conductState, caps, scopeLabel) ? <PoolBlocked text={poolBlocker(conductState, caps, scopeLabel)} error={conductState.status === "error"} /> : null}
              poolLine={<PoolLine pool={conductState} />}
              onOpenSession={({ session, message = null }) => openSessionAt({ session, message })}
              onGoTriage={() => setTab("lite")}
              focus={investigateFocus}
            />
          ) : (
            <>
          <div className="grid gap-[8px]" aria-busy={loading}>
            <div
              className="sticky z-20 grid gap-[8px] bg-[color:var(--bg)] pt-[8px] pb-[8px] shadow-[0_1px_0_var(--line-soft)] max-[900px]:static max-[900px]:shadow-none"
              style={{ top: 0 }}
            >
              <div className="grid items-start gap-[8px] [grid-template-columns:minmax(0,1fr)_152px] max-[900px]:grid-cols-1">
                <div className="grid gap-[5px]">
                  <span className={NOTE_CLASS}>Search</span>
                  <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder="Filter traces: summary, endpoint, trace id"
                    label="Search traces"
                    size="sm"
                  />
                </div>
                <label className="grid gap-[5px]">
                  <span className={NOTE_CLASS}>Sort</span>
                  <Select
                    value={sort}
                    onChange={(v) => setSort(String(v))}
                    options={SESSION_SORT_OPTIONS.map(([value, label]) => ({ value, label }))}
                    ariaLabel="Sort traces"
                  />
                </label>
              </div>

              <div data-reviews-summary="" className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] border-y border-[color:var(--line-soft)] py-[6px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
                {summary.map((part) => (
                  <span key={part.label}>
                    {part.value != null ? <><strong className="text-[color:var(--ink)]">{part.value.toLocaleString()}</strong>{" "}</> : null}
                    {part.label}
                  </span>
                ))}
                {loading ? <span>reading reviews</span> : null}
              </div>

              <div className="overflow-hidden">
                <div className="will-change-transform" style={{ minWidth: `${minWidth}px` }}>
                  <div
                    className="grid items-center gap-x-2 border-y border-[color:var(--line-soft)] px-[6px] py-[4px] text-[12px] leading-[1.08] text-[color:var(--muted)]"
                    style={{ gridTemplateColumns: cols.gridCols }}
                  >
                    {cols.effective.map((c) => (<span key={c.id}>{c.label}</span>))}
                  </div>
                </div>
              </div>
            </div>

            {!note && !sensitive && rows.length ? <span className={`${NOTE_CLASS} block px-[6px]`}>{SENSITIVE_NOTE}</span> : null}
            {note ? (
              <div role={sessions.status === "error" ? "alert" : "status"} className="grid gap-[4px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[12px]">
                <strong className="text-[13px] leading-[1.08] font-medium">{note}</strong>
              </div>
            ) : rows.length || loading ? (
              <div data-trace-log-scroll ref={wrapRef} className="relative min-w-0">
                <DataTable
                  cols={cols}
                  rows={rows}
                  rowAs="div"
                  getRowId={(row) => row.rowId}
                  renderCell={renderCell}
                  renderFullRow={renderFullRow}
                  loading={loading}
                  showHeader={false}
                  findScrollContainer={findTraceScrollRoot}
                  minWidth={`${minWidth}px`}
                  onRowClick={(row) => {
                    if (row.kind === "session") toggle(row.session.id);
                    else if (row.kind === "finding" || row.kind === "dropped-finding") toggleFinding(row.rowId);
                    else if (row.kind === "dropped") toggleDropped(row.session.id);
                  }}
                  rowClassName={rowClass}
                  footer={footer}
                />
                {picked && place ? (
                  <BinPopover
                    innerRef={popRef}
                    place={place}
                    bin={picked.bin}
                    turns={picked.turns}
                    indexOf={(id) => picked.order.get(id)}
                    onChoose={(findingId) => chooseFinding(pickedBin.caseId, findingId)}
                    onClose={() => setPickedBin(null)}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
